import { Localization, SessionContext } from "@viamap/viamap2-common";
import JSZip from "jszip";
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { utils as XlsxUtils, write as writeXlsx } from "xlsx";
import { LayerInfo } from "src/common/managers/Types";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { DC } from "src/dataCollector/DataCollector";
import { ExportData } from "src/managers/ExportData";
import { PropertyInformation } from "src/managers/PropertyInformation";
import { getServitutDownloadFileName } from "src/managers/ServitutDownloadNaming";
import {
   fetchTingbogDocumentBlob,
   getTingbogDownloadTarget,
   isTingbogElectronicDocument,
   parseTinglysningsDatoToUtcMs,
} from "src/managers/TingbogDownload";
import {
   addServitutPackageToZip,
   getServitutAnnotationDocumentId,
   isEligibleDigitalServitut,
   type ServitutElectronicPackageServitut,
} from "src/propertyInfoTemplates/TingbogServitutBatch";
import { cutPdfToSelectedPages } from "src/modules/ANNModule/ANNPDFCutter";
import { createANNLLMAnalyzer, getANNLLMAnalyzer, getServitutPdfFetchAktId, isServitutFromOvrigAktSource, type LLMAnalysisResult, type LLMAnalysisResultItem, type LLMMetadata } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { normalizeServitutIdentity } from "src/modules/ANNModule/ANNServitutIdentity";
import { downloadOverlayImage, uploadOverlayImage } from "src/modules/ANNModule/ANNImageOverlayStorage";
import { ANNManager } from "src/modules/ANNModule/ANNManager";
import { findServitutDocumentAnnotation } from "src/modules/ANNModule/ANNServitutDocumentAnnotations";
import { useSaveAnnotation } from "src/modules/ANNModule/ANNState";
import { AnnotationContextType, AnnotationType, type ANNAnnotationRecord } from "src/modules/ANNModule/ANNTypes";
import { MapitStateContext } from "src/states/MapitState";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { GlassButton, GlassButtonRow } from "./GlassButtons";
import { GlassInputControl, GlassInputGroup } from "./GlassInput";
import { message } from "./Message";
import { VLine } from "./MitGlassComponents";
import { hentServitutDataOgBeliggenhed, SFEAndColorList, type ServitutRapportBfeData, type SFEColorEntry } from "./ServitutRapportData";
import { aktIdFormat } from "./DatarapportShared";
import type { PropertyData } from "src/propertyInfoTemplates/PropertyInfoCollectorTypes";
import { Feature, FeatureType } from "src/states/ApplicationState";
import "./ServitutRedegoerelseRapport.css";

type ServitutRedegoerelsePreparedAkt = {
   document: string;
   aktId: string;
   documentUrl: string;
   source: "servitutAkt" | "ovrigAkt";
   servitutKeys: string[];
};

/** Fremdrift ved ZIP-eksport (Excel → hvert dokument → komprimering). */
export type ServitutRedegoerelseExportProgress = {
   completedSteps: number;
   totalSteps: number;
   stage: "spreadsheet" | "document" | "compress";
   activeRowKey: string | null;
   /** 1-based indeks blandt dokumenter (kun ved stage document). */
   documentIndex?: number;
   documentTotal?: number;
};

/** Legacy indskannede akt-Samlinger kræver LLM + sidevalg; digitale tingbogsdokumenter pakkes ved eksport som i ejendomsinfo (akt + SERVIMAP + bilag). */
export type ServitutRedegoerelseRowHandling = "legacyLlmPdf" | "electronicZipPackage";
export type ServitutRedegoerelseDocumentSource = "tingbog" | "annotation" | "rigsarkivetRequired";

type ServitutMatrixRow = {
   key: string;
   servitut: any;
   handling: ServitutRedegoerelseRowHandling;
   displayAktId: string;
   datoLoebenr: string;
   priority: string;
   summary: string;
   analysisAktId: string;
   includedBfes: Set<number>;
   pdfStatus: "pending" | "ready" | "missing" | "failed";
   pdfSource?: ServitutRedegoerelsePreparedAkt["source"];
   aktDocument?: string;
   aktId?: string;
   documentUrl?: string;
   analysisItem?: LLMAnalysisResultItem;
   selectedPages?: number[];
   exportFileName?: string;
   documentSource?: ServitutRedegoerelseDocumentSource;
   annotationRecord?: ANNAnnotationRecord;
   annotationDocument?: ANNAnnotationRecord["document"];
   sourceMessage?: string;
};

/** Per-BFE metadata for matrix headers (full matrikel list + ejerlav); ejerlav not shown inline on the column. */
export type ServitutMatrixColumnDetail = {
   matrikler: string[];
   ejerlav?: string;
};

type ServitutRedegoerelsePayload = {
   metadata: LLMMetadata[];
   caseDescription?: string;
   analysisResult?: LLMAnalysisResult;
   matrix: {
      columns: number[];
      columnLabels?: Record<string, string>;
      /** Full cadastral breakdown per column for hover popover (keys are BFE numbers as strings). */
      columnDetails?: Record<string, ServitutMatrixColumnDetail>;
      uniqueAktCount: number;
      rows: {
         key: string;
         servitut?: any;
         /** Standard: eksisterende rapporter — behandles som scanned akt + LLM. */
         handling?: ServitutRedegoerelseRowHandling;
         electronicServitut?: ServitutElectronicPackageServitut;
         displayAktId: string;
         datoLoebenr: string;
         priority: string;
         summary: string;
         analysisAktId: string;
         includedBfes: number[];
         pdfStatus: ServitutMatrixRow["pdfStatus"];
         pdfSource?: ServitutRedegoerelsePreparedAkt["source"];
         aktDocument?: string;
         aktId?: string;
         documentUrl?: string;
         analysisItem?: LLMAnalysisResultItem;
         selectedPages?: number[];
         exportFileName?: string;
         documentSource?: ServitutRedegoerelseDocumentSource;
         annotationRecord?: ANNAnnotationRecord;
         annotationDocument?: ANNAnnotationRecord["document"];
         sourceMessage?: string;
      }[];
   };
};

export type ServitutRedegoerelseWindowParams = {
   payload?: ServitutRedegoerelsePayload;
   /** When true, AI analysis starts automatically once the window opens with a prepared payload. */
   autoStartAnalysis?: boolean;
};

const DEFAULT_SERVITUT_BFE_COLOR = "#2563eb";

type ServitutBfeHeaderStyle = CSSProperties & {
   "--servitut-bfe-head-width": string;
   "--servitut-bfe-head-height": string;
};

const SERVITUT_ANALYSIS_CONCURRENCY = 10;
const SERVITUT_REDEGOERELSE_SESSION_CACHE_KEY = "servitut-redegoerelse:last-report";

type ServitutRedegoerelseSessionCache = {
   payload: ServitutRedegoerelsePayload;
   validatedKeys: string[];
   savedAt: string;
};

function loadServitutRedegoerelseSessionCache(): ServitutRedegoerelseSessionCache | null {
   try {
      const raw = sessionStorage.getItem(SERVITUT_REDEGOERELSE_SESSION_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<ServitutRedegoerelseSessionCache>;
      if (!parsed.payload?.matrix?.rows || !Array.isArray(parsed.validatedKeys)) {
         return null;
      }
      return parsed as ServitutRedegoerelseSessionCache;
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to restore session cache", error);
      return null;
   }
}

function saveServitutRedegoerelseSessionCache(
   payload: ServitutRedegoerelsePayload,
   validatedKeys: Set<string>
) {
   try {
      const cache: ServitutRedegoerelseSessionCache = {
         payload: {
            ...payload,
            // Raw XML/PDF metadata can be large; the restored UI only needs the prepared matrix/results.
            metadata: [],
         },
         validatedKeys: Array.from(validatedKeys),
         savedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(SERVITUT_REDEGOERELSE_SESSION_CACHE_KEY, JSON.stringify(cache));
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to save session cache", error);
   }
}

export function ServitutRedegoerelseRapport(props: {
   currentRapport: string,
   availableRapports: { value: string, label: string }[],
   onSelectRapport: (a: string) => void,
   onComplete?: () => void
}) {
   const { state: mapitState } = useContext(MapitStateContext);
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const [loading, setLoading] = useState(false);

   const usableLayers = useMemo(() => {
      return Object.values(mapitState.layers).filter((layer): layer is LayerInfo => layer.propertiesInGeoJson?.includes("SFEnummer") ?? false);
   }, [mapitState.layers]);

   const [layerId, setLayerId] = useState<number[]>(usableLayers.map((a) => a.layerId));

   useEffect(() => {
      setLayerId((a) => a.filter((b) => usableLayers.map((layer) => layer.layerId).includes(b)));
   }, [mapitState.layers, usableLayers]);

   const colorList = useMemo(() => {
      return SFEAndColorList(
         layerId.map((a) => mapitState.layers[a]).filter(Boolean) as LayerInfo[]
      );
   }, [layerId, mapitState.layers]);

   async function prepareRedegoerelse() {
      setLoading(true);
      try {
         const payload = await prepareServitutRedegoerelseForColorList(colorList, {
            organizationId: sessionState?.customerRef,
         });
         windowDispatch(openWindow(WindowId.ServitutRedegoerelse, {
            payload,
         }));
         props.onComplete?.();
      } catch (e: unknown) {
         if (e instanceof Error) {
            message.error(Localization.getFormattedText("An error occurred while generating the report. Please try again later or contact support. Error details: {err}", { err: e.message }));
         } else {
            message.error(Localization.getText("An error occurred while generating the report. Please try again later or contact support."));
         }
      } finally {
         setLoading(false);
      }
   }
   return (
      <>
         <ADModalBody>
            <GlassInputGroup>
               <label>{Localization.getText("Document")}</label>
               <GlassInputControl>
                  <select
                     disabled={false}
                     value={props.currentRapport}
                     onChange={(e) => {
                        props.onSelectRapport(e.currentTarget.value);
                     }}
                  >
                     {props.availableRapports.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                     ))}
                  </select>
               </GlassInputControl>
               <label>{Localization.getText("Property layer")}</label>
               <Typeahead
                  multiple={true}
                  id="servitutRedegoerelseLayerTypeahead"
                  options={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname }))}
                  labelKey={"datasetname"}
                  placeholder={Localization.getText("please select") + " " + Localization.getText("Property layer")}
                  emptyLabel={Localization.getText("No results found")}
                  onChange={(selected) => {
                     setLayerId(selected.map((a: any) => a.id));
                  }}
                  selected={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname })).filter((a) => layerId.includes(a.id))}
               />
               <label></label>
               <GlassButtonRow>
                  <GlassButton onClick={() => setLayerId(usableLayers.map((a) => a.layerId))}>{Localization.getText("Select All")}</GlassButton>
                  <GlassButton onClick={() => setLayerId([])}>{Localization.getText("Remove All")}</GlassButton>
               </GlassButtonRow>
               <VLine color={"#9d9d9d"} /><VLine color={"#9d9d9d"} />
               <label>{Localization.getText("Properties")} {colorList.length}</label>
               <div style={{ display: "flex", flexWrap: "wrap", gap: "2px" }}>
                  {colorList.map((a) => (
                     <div
                        key={a.sfe}
                        title={a.sfe + ""}
                        style={{ background: a.color, borderRadius: "5px", padding: "3px", display: "flex", justifyContent: "center" }}
                     >
                        <MIBIcon icon="property" style={{ aspectRatio: "1", color: "white" }} />
                     </div>
                  ))}
               </div>
            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter>
            {loading ? <Spinner /> : (
               <GlassButton disabled={!colorList.length} onClick={prepareRedegoerelse}>
                  {Localization.getText("Generate")}
               </GlassButton>
            )}
         </ADModalFooter>
      </>
   );
}

function buildColorListForBfes(bfes: number[]): SFEColorEntry[] {
   return bfes.map((sfe) => ({ sfe, color: DEFAULT_SERVITUT_BFE_COLOR }));
}

function getPropertyBfeFromData(propertyData: Partial<PropertyData>): number | null {
   const value =
      propertyData?.BBRRel?.bfeNummer ??
      propertyData?.BBRSFE?.[0]?.properties?.BFEnummer ??
      propertyData?.ViamapEjendomData?.sag?.BFEnr ??
      propertyData?.GQL_BBR?.MAT_SamletFastEjendom?.nodes?.[0]?.BFEnummer;
   const parsed = parseInt(`${value || ""}`, 10);
   return Number.isFinite(parsed) ? parsed : null;
}

export async function prepareServitutRedegoerelseForColorList(
   colorList: SFEColorEntry[],
   options: { organizationId?: string } = {},
): Promise<ServitutRedegoerelsePayload> {
   const data = await hentServitutDataOgBeliggenhed(colorList);
   return prepareServitutRedegoerelsePayload(data, colorList, options);
}

export async function prepareServitutRedegoerelseForBfes(
   bfes: number[],
   options: { organizationId?: string } = {},
): Promise<ServitutRedegoerelsePayload> {
   const uniqueBfes = Array.from(new Set(bfes.filter((bfe) => Number.isFinite(bfe))));
   return prepareServitutRedegoerelseForColorList(buildColorListForBfes(uniqueBfes), options);
}

export function ServitutRedegoerelseLaunchButton(props: {
   propertyData: Partial<PropertyData>;
   hasAccessToFeature: (feature: FeatureType) => boolean;
   autoStartAnalysis?: boolean;
   onMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
}) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { state: sessionState } = useContext(SessionContext);
   const [loading, setLoading] = useState(false);
   const propertyBfe = getPropertyBfeFromData(props.propertyData);
   const canUse = props.hasAccessToFeature(Feature.ServitutRedegoerelse);

   if (!canUse || propertyBfe === null) {
      return null;
   }
   const bfeNumber = propertyBfe;

   async function openServitutRedegoerelse(event: React.MouseEvent<HTMLElement>) {
      event.preventDefault();
      event.stopPropagation();
      if (loading) {
         return;
      }
      setLoading(true);
      message.progress(Localization.getText("Preparing easement review from land registry…"));
      await new Promise<void>((resolve) => {
         requestAnimationFrame(() => resolve());
      });
      try {
         const payload = await prepareServitutRedegoerelseForBfes([bfeNumber], {
            organizationId: sessionState?.customerRef,
         });
         windowDispatch(openWindow(WindowId.ServitutRedegoerelse, {
            payload,
            autoStartAnalysis: props.autoStartAnalysis ?? true,
         }));
      } catch (error: unknown) {
         if (error instanceof Error) {
            message.error(Localization.getFormattedText("An error occurred while generating the report. Please try again later or contact support. Error details: {err}", { err: error.message }));
         } else {
            message.error(Localization.getText("An error occurred while generating the report. Please try again later or contact support."));
         }
      } finally {
         message.progress();
         setLoading(false);
      }
   }

   return (
      <GlassButton
         title={Localization.getText("Open easement review for this property and start AI analysis automatically.")}
         disabled={loading}
         onMouseDown={props.onMouseDown}
         onClick={(event) => {
            void openServitutRedegoerelse(event);
         }}
         style={{
            marginLeft: 0,
            minHeight: 28,
            height: 28,
            padding: "2px 8px",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
         }}
      >
         {loading ? (
            <>
               <Spinner animation="border" role="status" size="sm" />
               <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  {Localization.getText("Preparing easement review from land registry…")}
               </span>
            </>
         ) : (
            <>
               <MIBIcon icon="list" />
               <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  {Localization.getText("Review easements")}
               </span>
            </>
         )}
      </GlassButton>
   );
}

export function ServitutRedegoerelseWindow(props: { managedWindow: ManagedWindow }) {
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const saveAnnotation = useSaveAnnotation();
   const windowParams = props.managedWindow.windowParams as ServitutRedegoerelseWindowParams | undefined;
   const initialPayload = windowParams?.payload;
   const autoStartAnalysis = Boolean(windowParams?.autoStartAnalysis);
   const shouldUseFreshPayload = Boolean(initialPayload);
   const autoStartAttemptedRef = useRef(false);
   const analysisAbortControllerRef = useRef<AbortController | null>(null);
   const [prepared, setPrepared] = useState<ServitutRedegoerelsePayload | null>(() => {
      if (shouldUseFreshPayload) {
         return initialPayload ?? null;
      }
      return loadServitutRedegoerelseSessionCache()?.payload ?? initialPayload ?? null;
   });
   const [analyzing, setAnalyzing] = useState(false);
   const [validatedKeys, setValidatedKeys] = useState<Set<string>>(() => {
      if (shouldUseFreshPayload) {
         return new Set();
      }
      return new Set(loadServitutRedegoerelseSessionCache()?.validatedKeys ?? []);
   });
   const [openingKey, setOpeningKey] = useState<string | null>(null);
   const [exporting, setExporting] = useState(false);
   const [exportProgress, setExportProgress] = useState<ServitutRedegoerelseExportProgress | null>(null);
   const [savingAnnotations, setSavingAnnotations] = useState(false);
   const [reevaluatingKeys, setReevaluatingKeys] = useState<Set<string>>(new Set());
   const [caseDescription, setCaseDescription] = useState(() => prepared?.caseDescription ?? "");
   const [caseDescriptionLocked, setCaseDescriptionLocked] = useState(() => hasPayloadAnalysis(prepared));

   async function yieldExportPaint(): Promise<void> {
      await new Promise<void>((resolve) => {
         requestAnimationFrame(() => resolve());
      });
   }

   useEffect(() => {
      if (!initialPayload) return;
      setPrepared(initialPayload);
      setValidatedKeys(new Set());
      setCaseDescription(initialPayload.caseDescription ?? "");
      setCaseDescriptionLocked(hasPayloadAnalysis(initialPayload));
   }, [initialPayload]);

   useEffect(() => {
      if (!prepared) return;
      saveServitutRedegoerelseSessionCache(prepared, validatedKeys);
   }, [prepared, validatedKeys]);

   useEffect(() => {
      autoStartAttemptedRef.current = false;
   }, [initialPayload, autoStartAnalysis]);

   useEffect(() => {
      return () => {
         analysisAbortControllerRef.current?.abort();
      };
   }, []);

   async function startAnalysis() {
      if (!prepared) return;
      if (!hasAnalyzableServitutRows(prepared)) {
         message.error(Localization.getText("There are no prepared documents for AI analysis in this view."));
         return;
      }
      const payloadForAnalysis = applyCaseDescriptionToPayload(prepared, caseDescription);
      setPrepared(payloadForAnalysis);
      setCaseDescriptionLocked(true);
      await analyzePayload(payloadForAnalysis);
   }

   useEffect(() => {
      if (!autoStartAnalysis || !prepared || autoStartAttemptedRef.current) {
         return;
      }
      if (analyzing || hasPayloadAnalysis(prepared) || !hasAnalyzableServitutRows(prepared)) {
         return;
      }
      autoStartAttemptedRef.current = true;
      void startAnalysis();
   }, [autoStartAnalysis, prepared, analyzing]);

   async function analyzePayload(payload: ServitutRedegoerelsePayload) {
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      const abortController = new AbortController();
      analysisAbortControllerRef.current?.abort();
      analysisAbortControllerRef.current = abortController;
      setAnalyzing(true);
      try {
         const analyzer = createANNLLMAnalyzer({
            userRef: sessionState.userRef,
            userName: sessionState.userName,
            organizationId: sessionState.customerRef,
         });
         const metadataBatches = groupMetadataByAkt(payload.metadata);
         let failedBatches = 0;
         await runWithConcurrency(metadataBatches, SERVITUT_ANALYSIS_CONCURRENCY, async (metadataBatch) => {
            if (abortController.signal.aborted) return;
            try {
               const result = await analyzer.analyzePdfUrlBatch(metadataBatch, abortController.signal);
               if (abortController.signal.aborted) return;
               setPrepared((currentPayload) => {
                  return attachAnalysisResult(currentPayload ?? payload, result);
               });
            } catch (error) {
               if (isAbortError(error)) return;
               failedBatches += 1;
               console.error("[Servitutredegørelse] AI akt batch failed", metadataBatch, error);
            }
         }, abortController.signal);
         await analyzeAnnotationRows(payload, analyzer, abortController.signal, (result) => {
            if (abortController.signal.aborted) return;
            setPrepared((currentPayload) => attachAnalysisResult(currentPayload ?? payload, {
               items: [result],
               total_processed: 1,
               total_requested: 1,
               timestamp: new Date().toISOString(),
            }));
         }, () => {
            failedBatches += 1;
         });
         if (abortController.signal.aborted) {
            message.info(Localization.getText("AI analysis stopped."));
            return;
         }
         if (failedBatches) {
            message.error(Localization.getFormattedText("{count} akt batches could not be analyzed.", { count: failedBatches }));
         }
      } catch (error) {
         if (isAbortError(error) || abortController.signal.aborted) {
            message.info(Localization.getText("AI analysis stopped."));
            return;
         }
         console.error("[Servitutredegørelse] AI analysis failed", error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to analyze PDF"));
      } finally {
         if (analysisAbortControllerRef.current === abortController) {
            analysisAbortControllerRef.current = null;
         }
         setAnalyzing(false);
      }
   }

   function stopAnalysis() {
      analysisAbortControllerRef.current?.abort();
   }

   async function openValidationViewer(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) {
      const handling = row.handling ?? "legacyLlmPdf";
      if (row.documentSource === "annotation") {
         if (!row.annotationDocument?.s3Key) {
            message.error(Localization.getText("No PDF available for this document"));
            return;
         }
         setOpeningKey(row.key);
         try {
            const { blob } = await downloadOverlayImage(row.annotationDocument.s3Key);
            const pdfUrl = URL.createObjectURL(blob);
            windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
               pdfUrl,
               pdfBlob: blob,
               analysisResult: row.analysisItem ? {
                  items: [row.analysisItem],
                  total_processed: 1,
                  total_requested: 1,
                  timestamp: new Date().toISOString(),
               } : undefined,
               aktId: row.aktId || row.analysisAktId,
               documentId: getCanonicalServitutAnnotationDocumentId(row),
               contextType: AnnotationContextType.Servitut,
               organizationId: sessionState?.customerRef,
               userId: sessionState?.userRef,
               userName: sessionState?.userName,
               documentFilename: row.annotationDocument.filename || row.exportFileName || row.aktDocument,
               initialSelectedPages: row.selectedPages,
               onCloseSelectedPages: (pages: number[]) => updateRowSelectedPages(row.key, pages),
            }));
         } catch (error) {
            message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
         } finally {
            setOpeningKey(null);
         }
         return;
      }
      if (handling === "electronicZipPackage") {
         const srv = row.electronicServitut;
         const target = srv ? getTingbogDownloadTarget(srv) : undefined;
         if (!srv || !target) {
            message.error(Localization.getText("No PDF available for this document"));
            return;
         }
         setOpeningKey(row.key);
         try {
            const pdfBlob = await fetchTingbogDocumentBlob(target);
            const pdfUrl = URL.createObjectURL(pdfBlob);
            windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
               pdfUrl,
               pdfBlob,
               analysisResult: undefined,
               aktId: srv.aktId || srv.aktAliasId || row.analysisAktId,
               documentId: srv.dokumentId || srv.aktAliasId || row.key,
               contextType: AnnotationContextType.Servitut,
               organizationId: sessionState?.customerRef,
               userId: sessionState?.userRef,
               userName: sessionState?.userName,
               documentFilename: row.exportFileName || `${row.displayAktId}.pdf`,
            }));
         } catch (error) {
            message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
         } finally {
            setOpeningKey(null);
         }
         return;
      }

      if (!row.aktId) {
         message.error(Localization.getText("No PDF available for this document"));
         return;
      }
      setOpeningKey(row.key);
      try {
         const analyzer = getANNLLMAnalyzer();
         const pdfBlob = await analyzer.fetchPdf(row.aktId);
         const pdfUrl = URL.createObjectURL(pdfBlob);
         windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
            pdfUrl,
            pdfBlob,
            analysisResult: row.analysisItem ? {
               items: [row.analysisItem],
               total_processed: 1,
               total_requested: 1,
               timestamp: new Date().toISOString(),
            } : undefined,
            aktId: row.aktId,
            documentId: getCanonicalServitutAnnotationDocumentId(row),
            contextType: AnnotationContextType.Servitut,
            organizationId: sessionState?.customerRef,
            userId: sessionState?.userRef,
            userName: sessionState?.userName,
            documentFilename: row.aktDocument,
            initialSelectedPages: row.selectedPages,
            onCloseSelectedPages: (pages: number[]) => updateRowSelectedPages(row.key, pages),
         }));
      } catch (error) {
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
      } finally {
         setOpeningKey(null);
      }
   }

   function updateRowSelectedPages(key: string, pages: number[]) {
      const normalizedPages = normalizePages(pages);
      const previousRow = prepared?.matrix.rows.find((row) => row.key === key);
      setPrepared((currentPayload) => {
         if (!currentPayload) return currentPayload;
         return {
            ...currentPayload,
            matrix: {
               ...currentPayload.matrix,
               rows: currentPayload.matrix.rows.map((row) => (
                  row.key === key ? { ...row, selectedPages: normalizedPages } : row
               )),
            },
         };
      });
      if (
         previousRow?.analysisItem &&
         !arePageSelectionsEqual(previousRow.analysisItem.pages_kept || [], normalizedPages)
      ) {
         windowDispatch(openWindow(WindowId.ConfirmationDialog, {
            header: "Recalculate AI analysis",
            children: Localization.getText("The selected pages differ from the AI analysis. Recalculate the AI fields for this selection?"),
            onSubmit: () => {
               void reevaluateRowForSelectedPages(previousRow, normalizedPages);
            },
         }));
      }
   }

   async function reevaluateRowForSelectedPages(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      pages: number[],
   ) {
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      setReevaluatingKeys((prev) => new Set(prev).add(row.key));
      try {
         const analyzer = createANNLLMAnalyzer({
            userRef: sessionState.userRef,
            userName: sessionState.userName,
            organizationId: sessionState.customerRef,
         });
         const metadata = buildRowEvaluationMetadata(row, prepared, caseDescription);
         const pdfBlob = await getRowSourcePdfBlob(row);
         if (!pdfBlob) {
            throw new Error(Localization.getText("No PDF available for this document"));
         }
         const documentFileName =
            row.annotationDocument?.filename ||
            row.exportFileName ||
            row.aktDocument ||
            "servitut.pdf";
         const result = await analyzer.evaluateServitutDocument({
            metadata,
            pages,
            pdfBlob,
            documentFileName,
         });
         setPrepared((currentPayload) => currentPayload ? attachAnalysisResult(currentPayload, {
            items: [{ ...result, id: row.key }],
            total_processed: 1,
            total_requested: 1,
            timestamp: new Date().toISOString(),
         }) : currentPayload);
         message.info(Localization.getText("AI analysis updated for selected pages."));
      } catch (error) {
         console.error("[Servitutredegørelse] page selection re-evaluation failed", row.key, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to analyze PDF"));
      } finally {
         setReevaluatingKeys((prev) => {
            const next = new Set(prev);
            next.delete(row.key);
            return next;
         });
      }
   }

   function toggleValidated(key: string) {
      setValidatedKeys((prev) => {
         const next = new Set(prev);
         if (next.has(key)) {
            next.delete(key);
         } else {
            next.add(key);
         }
         return next;
      });
   }

   async function exportBehandlede() {
      if (!prepared) return;
      const rows = prepared.matrix.rows.filter((row) => validatedKeys.has(row.key));
      if (!rows.length) {
         message.error(Localization.getText("There are no treated easements to export."));
         return;
      }

      const totalSteps = 2 + rows.length;
      setExporting(true);
      setExportProgress({
         completedSteps: 0,
         totalSteps,
         stage: "spreadsheet",
         activeRowKey: null,
      });
      await yieldExportPaint();
      try {
         const zipBlob = await buildServitutRedegoerelseZip(prepared, validatedKeys, async (prog) => {
            setExportProgress(prog);
            await yieldExportPaint();
         });
         ExportData.downloadBlob(`Servitutredegørelse_${Date.now()}.zip`, zipBlob);
         message.info(Localization.getFormattedText("{count} treated easements have been exported.", { count: rows.length }));
      } catch (error) {
         console.error("[Servitutredegørelse] export failed", error);
         message.error(error instanceof Error ? error.message : Localization.getText("Export failed"));
      } finally {
         setExporting(false);
         setExportProgress(null);
      }
   }

   async function saveTreatedAsAnnotations() {
      if (!prepared) return;
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      const rows = prepared.matrix.rows.filter((row) => (
         validatedKeys.has(row.key) && canSaveServitutRowAsAnnotation(row)
      ));
      if (!rows.length) {
         message.error(Localization.getText("There are no treated easements to save."));
         return;
      }
      windowDispatch(openWindow(WindowId.ConfirmationDialog, {
         header: "Save annotations",
         children: Localization.getFormattedText("Save {count} treated easements as annotations?", { count: rows.length }),
         onSubmit: () => {
            void saveRowsAsAnnotations(rows);
         },
      }));
   }

   async function saveRowsAsAnnotations(rows: ServitutRedegoerelsePayload["matrix"]["rows"]) {
      setSavingAnnotations(true);
      let savedCount = 0;
      let skippedCount = 0;
      let failedCount = 0;
      for (const row of rows) {
         if (row.annotationRecord || row.documentSource === "annotation") {
            skippedCount += 1;
            continue;
         }
         try {
            const sourcePdf = await getRowSourcePdfBlob(row);
            if (!sourcePdf) {
               skippedCount += 1;
               continue;
            }
            const pagesToKeep = normalizePages(row.selectedPages || []);
            const documentBlob = row.handling === "electronicZipPackage"
               ? sourcePdf
               : pagesToKeep.length
                  ? await cutPdfToSelectedPages(sourcePdf, pagesToKeep)
                  : sourcePdf;
            const filename = row.exportFileName || `${row.displayAktId || row.key}.pdf`;
            const file = new File([documentBlob], filename, { type: "application/pdf" });
            const uploadResult = await uploadOverlayImage(file);
            const documentId = getCanonicalServitutAnnotationDocumentId(row);
            const record = await saveAnnotation({
               type: AnnotationType.Document,
               documentId,
               contextType: AnnotationContextType.Servitut,
               organizationId: sessionState.customerRef,
               userId: sessionState.userRef,
               userName: sessionState.userName,
               datoloebenr: row.datoLoebenr,
               document: {
                  filename,
                  s3Key: uploadResult.key,
                  s3Url: uploadResult.url,
                  mimeType: "application/pdf",
                  size: documentBlob.size,
               },
               title: filename.replace(/\.pdf$/i, ""),
               historyComment: pagesToKeep.length
                  ? `Saved from servitutredegørelse. Pages: ${pagesToKeep.join(", ")}`
                  : "Saved from servitutredegørelse.",
               annotationType: "Dokument",
            });
            savedCount += 1;
            setPrepared((currentPayload) => currentPayload ? {
               ...currentPayload,
               matrix: {
                  ...currentPayload.matrix,
                  rows: currentPayload.matrix.rows.map((currentRow) => currentRow.key === row.key ? {
                     ...currentRow,
                     annotationRecord: record,
                     annotationDocument: record.document,
                     documentSource: "annotation",
                     pdfStatus: "ready",
                     sourceMessage: Localization.getText("Using saved annotation PDF"),
                     aktDocument: record.document?.filename || currentRow.aktDocument,
                  } : currentRow),
               },
            } : currentPayload);
         } catch (error) {
            failedCount += 1;
            console.error("[Servitutredegørelse] failed to save annotation", row.key, error);
         }
      }
      message.info(Localization.getFormattedText("Saved {saved} annotations. Skipped {skipped}. Failed {failed}.", {
         saved: savedCount,
         skipped: skippedCount,
         failed: failedCount,
      }));
      setSavingAnnotations(false);
   }

   return (
      <AdvancedDragModal
         window={props.managedWindow}
         title={Localization.getText("Easement review")}
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
         allowFullscreen={true}
         allowUserMiniMize={true}
         allowPopout={false}
         PosDefault={{ top: "80px", left: "80px", width: "1100px", height: "720px" }}
      >
         <ADModalBody>
            {prepared ? (
               <ServitutMatrix
                  payload={prepared}
                  analyzing={analyzing}
                  openingKey={openingKey}
                  exporting={exporting}
                  exportProgress={exportProgress}
                  savingAnnotations={savingAnnotations}
                  reevaluatingKeys={reevaluatingKeys}
                  validatedKeys={validatedKeys}
                  onOpenRow={openValidationViewer}
                  onToggleValidated={toggleValidated}
                  onSetTreatedRows={(keys, treated) => {
                     setValidatedKeys((prev) => {
                        const next = new Set(prev);
                        keys.forEach((key) => {
                           if (treated) {
                              next.add(key);
                           } else {
                              next.delete(key);
                           }
                        });
                        return next;
                     });
                  }}
                  onAnalyze={startAnalysis}
                  onStopAnalysis={stopAnalysis}
                  canAnalyze={hasAnalyzableServitutRows(prepared)}
                  caseDescription={caseDescription}
                  caseDescriptionLocked={caseDescriptionLocked || analyzing}
                  onCaseDescriptionChange={setCaseDescription}
                  onExport={exportBehandlede}
                  onSaveAnnotations={saveTreatedAsAnnotations}
               />
            ) : <Spinner />}
         </ADModalBody>
      </AdvancedDragModal>
   );
}

function servitutExportProgressPercent(p: ServitutRedegoerelseExportProgress): number {
   if (!p.totalSteps) {
      return 0;
   }
   return Math.min(100, Math.round((p.completedSteps / p.totalSteps) * 100));
}

function servitutExportProgressCaption(p: ServitutRedegoerelseExportProgress): string {
   if (p.stage === "spreadsheet" && p.completedSteps === 0) {
      return Localization.getText("Preparing ZIP export…");
   }
   if (p.stage === "spreadsheet") {
      return Localization.getText("Adding Excel to the archive…");
   }
   if (p.stage === "compress" && p.completedSteps < p.totalSteps) {
      return Localization.getText("Compressing ZIP…");
   }
   if (p.stage === "compress") {
      return Localization.getText("Finishing…");
   }
   if (p.documentIndex !== undefined && p.documentTotal !== undefined) {
      return Localization.getFormattedText("Fetching and adding document {index} of {total}…", {
         index: p.documentIndex,
         total: p.documentTotal,
      });
   }
   return Localization.getText("Exporting…");
}

const BFE_HEAD_POPOVER_HIDE_MS = 140;

function ServitutRedegoerelseTextClamp(props: {
   className?: string;
   title?: string;
   children: React.ReactNode;
}) {
   const ref = useRef<HTMLDivElement>(null);
   const [overflows, setOverflows] = useState(false);

   const measureOverflow = useCallback(() => {
      const element = ref.current;
      if (!element) {
         return;
      }
      setOverflows(element.scrollHeight > element.clientHeight + 1);
   }, []);

   useLayoutEffect(() => {
      measureOverflow();
   }, [measureOverflow, props.children, props.title]);

   useEffect(() => {
      const element = ref.current;
      if (!element) {
         return;
      }
      const resizeObserver = new ResizeObserver(() => measureOverflow());
      resizeObserver.observe(element);
      return () => resizeObserver.disconnect();
   }, [measureOverflow]);

   const className = [
      "servitut-redegoerelse__textClamp",
      overflows ? "servitut-redegoerelse__textClamp--overflow" : "",
      props.className || "",
   ].filter(Boolean).join(" ");

   return (
      <div ref={ref} className={className} title={props.title}>
         {props.children}
      </div>
   );
}

function ServitutBfeColumnHeadCell(props: {
   bfe: number;
   shortLabel: string;
   popover: ServitutMatrixColumnDetail;
   ariaLabel: string;
}) {
   const wrapRef = useRef<HTMLDivElement | null>(null);
   const innerRef = useRef<HTMLSpanElement | null>(null);
   const [labelBox, setLabelBox] = useState<{ w: number; h: number } | null>(null);
   const [open, setOpen] = useState(false);
   const [pos, setPos] = useState({ top: 0, left: 0 });
   const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const measureLabel = useCallback(() => {
      const inner = innerRef.current;
      if (!inner) return;
      const r = inner.getBoundingClientRect();
      /** Axis-aligned bounds of the rotated label — drives column width and header row height. */
      const horizontalPad = 12;
      const bottomPad = 12;
      setLabelBox({
         w: Math.ceil(r.width) + horizontalPad,
         h: Math.ceil(r.height) + bottomPad,
      });
   }, []);

   useLayoutEffect(() => {
      measureLabel();
   }, [measureLabel, props.shortLabel]);

   useEffect(() => {
      const inner = innerRef.current;
      if (!inner) return;
      const ro = new ResizeObserver(() => measureLabel());
      ro.observe(inner);
      return () => ro.disconnect();
   }, [measureLabel]);

   useEffect(() => {
      const onResize = () => measureLabel();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
   }, [measureLabel]);

   useEffect(() => {
      const fonts = document.fonts;
      if (!fonts?.ready) return;
      let cancelled = false;
      fonts.ready.then(() => {
         if (!cancelled) measureLabel();
      });
      return () => {
         cancelled = true;
      };
   }, [measureLabel]);

   const clearHideTimer = () => {
      if (hideTimerRef.current !== null) {
         clearTimeout(hideTimerRef.current);
         hideTimerRef.current = null;
      }
   };

   const updatePosition = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const margin = 8;
      setPos({ top: r.bottom + margin, left: r.left });
   };

   const openPopover = () => {
      clearHideTimer();
      updatePosition();
      setOpen(true);
   };

   const scheduleClose = () => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => setOpen(false), BFE_HEAD_POPOVER_HIDE_MS);
   };

   useLayoutEffect(() => {
      if (!open) return;
      updatePosition();
   }, [open, props.shortLabel]);

   useEffect(() => {
      if (!open) return;
      const close = () => setOpen(false);
      window.addEventListener("scroll", close, true);
      window.addEventListener("resize", close);
      return () => {
         window.removeEventListener("scroll", close, true);
         window.removeEventListener("resize", close);
      };
   }, [open]);

   useEffect(() => () => clearHideTimer(), []);

   const popoverContent = (
      <div
         className="servitut-redegoerelse__bfePopover"
         style={{ top: pos.top, left: pos.left }}
         onMouseEnter={clearHideTimer}
         onMouseLeave={scheduleClose}
         role="tooltip"
      >
         <div className="servitut-redegoerelse__bfePopoverBfe">
            {Localization.getFormattedText("BFE {bfe}", { bfe: props.bfe })}
         </div>
         {props.popover.ejerlav ? (
            <div className="servitut-redegoerelse__bfePopoverEjerlav">
               <span className="servitut-redegoerelse__bfePopoverK">{Localization.getText("Cadastral district (ejerlav)")}</span>
               {props.popover.ejerlav}
            </div>
         ) : null}
         <div className="servitut-redegoerelse__bfePopoverMatrikelBlock">
            <span className="servitut-redegoerelse__bfePopoverK">{Localization.getText("Cadastral plots")}</span>
            <ul className="servitut-redegoerelse__bfePopoverList">
               {props.popover.matrikler.map((m, index) => (
                  <li key={`${props.bfe}-${index}-${m}`}>{m}</li>
               ))}
            </ul>
         </div>
      </div>
   );

   const thStyle: ServitutBfeHeaderStyle | undefined = labelBox
      ? ({
         "--servitut-bfe-head-width": `${labelBox.w}px`,
         "--servitut-bfe-head-height": `${labelBox.h}px`,
      } as ServitutBfeHeaderStyle)
      : undefined;

   return (
      <th
         className="servitut-redegoerelse__bfeHead"
         style={thStyle}
         aria-label={props.ariaLabel}
      >
         <div
            ref={wrapRef}
            className="servitut-redegoerelse__bfeHeadTrigger"
            onMouseEnter={openPopover}
            onMouseLeave={scheduleClose}
         >
            <span ref={innerRef} className="servitut-redegoerelse__bfeHeadInner">
               <span className="servitut-redegoerelse__bfeHeadMatrikel">{props.shortLabel}</span>
            </span>
         </div>
         {open ? createPortal(popoverContent, document.body) : null}
      </th>
   );
}

function ServitutMatrix(props: {
   payload: ServitutRedegoerelsePayload;
   analyzing: boolean;
   openingKey: string | null;
   exporting: boolean;
   exportProgress: ServitutRedegoerelseExportProgress | null;
   savingAnnotations: boolean;
   reevaluatingKeys: Set<string>;
   validatedKeys: Set<string>;
   onOpenRow: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onToggleValidated: (key: string) => void;
   onSetTreatedRows: (keys: string[], treated: boolean) => void;
   onAnalyze: () => void;
   onStopAnalysis: () => void;
   canAnalyze: boolean;
   caseDescription: string;
   caseDescriptionLocked: boolean;
   onCaseDescriptionChange: (value: string) => void;
   onExport: () => void;
   onSaveAnnotations: () => void;
}) {
   const [query, setQuery] = useState("");
   const [statusFilter, setStatusFilter] = useState<"all" | "found" | "missing" | "treated" | "untreated">("all");
   const [matrixSort, setMatrixSort] = useState<ServitutMatrixSortState>(null);
   const normalizedQuery = query.trim().toLowerCase();
   const hasCaseDescription = Boolean(props.payload.caseDescription?.trim());
   const filteredRows = useMemo(() => {
      const rows = props.payload.matrix.rows.filter((row) => {
         const handling = row.handling ?? "legacyLlmPdf";
         const analysisSearchText = [
            row.analysisItem?.servitut_type,
            row.analysisItem?.paataleberettiget,
            row.analysisItem?.case_relevance_reasoning,
            row.analysisItem?.overall_confidence_reasoning,
            ...(row.analysisItem?.review_reasons || []),
            formatCaseRelevant(row.analysisItem?.case_relevant),
         ].filter(Boolean).join(" ").toLowerCase();
         const matchesQuery = !normalizedQuery ||
            row.datoLoebenr.toLowerCase().includes(normalizedQuery) ||
            row.displayAktId.toLowerCase().includes(normalizedQuery) ||
            row.summary.toLowerCase().includes(normalizedQuery) ||
            analysisSearchText.includes(normalizedQuery) ||
            row.includedBfes.some((bfe) => `${bfe}`.includes(normalizedQuery));
         if (!matchesQuery) return false;
         if (statusFilter === "found") {
            return Boolean(row.analysisItem?.pages_kept?.length)
               || (handling === "electronicZipPackage" && row.pdfStatus === "ready");
         }
         if (statusFilter === "missing") {
            return handling === "legacyLlmPdf"
               && Boolean(row.analysisItem)
               && !(row.analysisItem?.pages_kept?.length);
         }
         if (statusFilter === "treated") return props.validatedKeys.has(row.key);
         if (statusFilter === "untreated") return !props.validatedKeys.has(row.key);
         return true;
      });
      return applyServitutMatrixSort(rows, props.payload.matrix.columns, hasCaseDescription, matrixSort);
   }, [hasCaseDescription, matrixSort, normalizedQuery, props.payload.caseDescription, props.payload.matrix.columns, props.payload.matrix.rows, props.validatedKeys, statusFilter]);
   const analyzedCount = props.payload.matrix.rows.filter((row) => Boolean(row.analysisItem)).length;
   const foundCount = props.payload.matrix.rows.filter((row) => Boolean(row.analysisItem?.pages_kept?.length)
      || ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage" && row.pdfStatus === "ready")).length;
   const highConfidenceCount = props.payload.matrix.rows.filter((row) => getReviewTriage(row).level === "good").length;
   const needsReviewCount = props.payload.matrix.rows.filter((row) => {
      const triage = getReviewTriage(row);
      return triage.level === "bad" || row.analysisItem?.needs_review === true;
   }).length;
   const treatedCount = props.validatedKeys.size;
   const saveableAnnotationCount = props.payload.matrix.rows.filter((row) => (
      props.validatedKeys.has(row.key) && canSaveServitutRowAsAnnotation(row)
   )).length;
   const filteredRowKeys = filteredRows.map((row) => row.key);
   const allFilteredRowsTreated = filteredRowKeys.length > 0 && filteredRowKeys.every((key) => props.validatedKeys.has(key));
   const cycleMatrixSort = (column: ServitutMatrixSortColumn) => {
      setMatrixSort((prev) => {
         if (!prev || prev.column !== column) {
            const direction = column === "confidence" ? "desc" : "asc";
            return { column, direction };
         }
         if (column === "confidence") {
            if (prev.direction === "desc") return { column, direction: "asc" };
            return null;
         }
         if (prev.direction === "asc") return { column, direction: "desc" };
         return null;
      });
   };
   const matrixSortIndicator = (column: ServitutMatrixSortColumn) => {
      if (!matrixSort || matrixSort.column !== column) return "↕";
      return matrixSort.direction === "asc" ? "↑" : "↓";
   };

   const exportProgressPct = props.exportProgress
      ? servitutExportProgressPercent(props.exportProgress)
      : 0;
   const exportProgressLabel = props.exportProgress
      ? servitutExportProgressCaption(props.exportProgress)
      : "";

   return (
      <div className="servitut-redegoerelse">
         <div className="servitut-redegoerelse__hero">
            <div>
               <div className="servitut-redegoerelse__eyebrow">{Localization.getText("Easement review")}</div>
               <h2>{Localization.getText("Review easements")}</h2>
               <p>{Localization.getText("Review the easement matrix first, then start AI analysis when you are ready. Use the safety colour to quickly prioritize expert review.")}</p>
            </div>
            <div className="servitut-redegoerelse__aiPanel">
               <div className={`servitut-redegoerelse__runState ${props.analyzing ? "servitut-redegoerelse__runState--active" : ""}`}>
                  <div className="servitut-redegoerelse__runStatus">
                     {props.analyzing ? <Spinner size="sm" /> : <span className="servitut-redegoerelse__dot" />}
                     <span>{props.analyzing ? Localization.getText("Analyzing with AI...") : analyzedCount ? Localization.getText("Analysis complete") : Localization.getText("Awaiting AI analysis")}</span>
                  </div>
                  <button
                     className={`servitut-redegoerelse__aiStart ${props.analyzing ? "servitut-redegoerelse__aiStart--stop" : ""}`}
                     disabled={!props.analyzing && (props.exporting || !props.canAnalyze)}
                     onClick={props.analyzing ? props.onStopAnalysis : props.onAnalyze}
                  >
                     {props.analyzing ? Localization.getText("Stop analysis") : Localization.getText("Start AI analysis")}
                  </button>
               </div>
               <label className="servitut-redegoerelse__casePanel">
                  <textarea
                     className="servitut-redegoerelse__caseInput"
                     value={props.caseDescription}
                     disabled={props.caseDescriptionLocked}
                     onChange={(e) => props.onCaseDescriptionChange(e.currentTarget.value)}
                     placeholder={Localization.getText("Optional: describe the specific issue that AI should assess for relevance.")}
                     rows={3}
                  />
               </label>
            </div>
         </div>

         <div className="servitut-redegoerelse__stats">
            <MetricCard label={Localization.getText("Easements")} value={props.payload.matrix.rows.length} />
            <MetricCard label={Localization.getText("AI processed")} value={`${analyzedCount}/${props.payload.matrix.rows.length}`} />
            <MetricCard label={Localization.getText("Documents found")} value={foundCount} tone="success" />
            <MetricCard label={Localization.getText("High confidence")} value={highConfidenceCount} tone="success" />
            <MetricCard label={Localization.getText("Needs review")} value={needsReviewCount} tone={needsReviewCount ? "error" : "success"} />
            <MetricCard label={Localization.getText("Treated")} value={`${treatedCount}/${props.payload.matrix.rows.length}`} tone="accent" />
         </div>

         <div className="servitut-redegoerelse__toolbar">
            <div className="servitut-redegoerelse__toolbarRow">
               <input
                  className="servitut-redegoerelse__search"
                  value={query}
                  onChange={(e) => setQuery(e.currentTarget.value)}
                  placeholder={Localization.getText("Search akt, text or BFE…")}
               />
               <select
                  className="servitut-redegoerelse__filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.currentTarget.value as typeof statusFilter)}
               >
                  <option value="all">{Localization.getText("All rows")}</option>
                  <option value="found">{Localization.getText("AI found pages")}</option>
                  <option value="missing">{Localization.getText("AI found no pages")}</option>
                  <option value="treated">{Localization.getText("Treated")}</option>
                  <option value="untreated">{Localization.getText("Not treated")}</option>
               </select>
               <button
                  className="servitut-redegoerelse__export"
                  disabled={props.exporting || props.validatedKeys.size === 0}
                  onClick={props.onExport}
               >
                  {props.exporting ? Localization.getText("Exporting...") : Localization.getFormattedText("Export treated ({count})", { count: props.validatedKeys.size })}
               </button>
               <button
                  className="servitut-redegoerelse__export servitut-redegoerelse__saveAnnotations"
                  disabled={props.savingAnnotations || props.exporting || saveableAnnotationCount === 0}
                  onClick={props.onSaveAnnotations}
               >
                  {props.savingAnnotations
                     ? Localization.getText("Saving annotations...")
                     : Localization.getFormattedText("Save approved as annotations ({count})", { count: saveableAnnotationCount })}
               </button>
            </div>
            {props.exporting && props.exportProgress ? (
               <div className="servitut-redegoerelse__exportProgress" aria-live="polite">
                  <div className="servitut-redegoerelse__exportProgressTrack">
                     <div
                        className="servitut-redegoerelse__exportProgressFill"
                        style={{ width: `${exportProgressPct}%` }}
                     />
                  </div>
                  <div className="servitut-redegoerelse__exportProgressFooter">
                     <Spinner animation="border" size="sm" className="servitut-redegoerelse__exportProgressSpinner" />
                     <span className="servitut-redegoerelse__exportProgressText">
                        {exportProgressPct}% — {exportProgressLabel}
                     </span>
                  </div>
               </div>
            ) : null}
         </div>

         <div className="servitut-redegoerelse__tableWrap">
            <table className="servitut-redegoerelse__table">
               <thead>
                  <tr>
                     <th className="servitut-redegoerelse__treatedHead">
                        <label className="servitut-redegoerelse__check servitut-redegoerelse__check--header">
                           <span className="servitut-redegoerelse__treatedHeadLabel">{Localization.getText("Treated")}</span>
                           <input
                              type="checkbox"
                              checked={allFilteredRowsTreated}
                              disabled={filteredRowKeys.length === 0 || props.exporting}
                              onChange={() => props.onSetTreatedRows(filteredRowKeys, !allFilteredRowsTreated)}
                           />
                        </label>
                     </th>
                     <th>
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("dato")}
                           title={Localization.getText("Sort by date / serial no. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("Date / serial no.")}</span>
                           <span>{matrixSortIndicator("dato")}</span>
                        </button>
                     </th>
                     <th>
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("priority")}
                           title={Localization.getText("Sort by priority. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("Priority")}</span>
                           <span>{matrixSortIndicator("priority")}</span>
                        </button>
                     </th>
                     <th>
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("aiSuggestion")}
                           title={Localization.getText("Sort by AI suggestion. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("AI suggestion")}</span>
                           <span>{matrixSortIndicator("aiSuggestion")}</span>
                        </button>
                     </th>
                     <th>
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("confidence")}
                           title={Localization.getText("Sort by AI confidence. Click again for highest first, lowest first and default sort.")}
                        >
                           <span>{Localization.getText("AI confidence")}</span>
                           <span>{matrixSortIndicator("confidence")}</span>
                        </button>
                     </th>
                     <th>
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("pagesKept")}
                           title={Localization.getText("Sort by pages kept. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("Pages kept")}</span>
                           <span>{matrixSortIndicator("pagesKept")}</span>
                        </button>
                     </th>
                     <th>
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("servitutType")}
                           title={Localization.getText("Sort by easement type. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("Easement type")}</span>
                           <span>{matrixSortIndicator("servitutType")}</span>
                        </button>
                     </th>
                     <th>
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("entitledParty")}
                           title={Localization.getText("Sort by entitled party. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("Entitled party")}</span>
                           <span>{matrixSortIndicator("entitledParty")}</span>
                        </button>
                     </th>
                     <th>
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("caseRelevant")}
                           title={Localization.getText("Sort by case relevance. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("Case relevant")}</span>
                           <span>{matrixSortIndicator("caseRelevant")}</span>
                        </button>
                     </th>
                     {props.payload.matrix.columns.map((bfe) => {
                        const label = getServitutMatrixColumnLabel(props.payload, bfe);
                        const shortHeaderText = getServitutMatrixColumnHeaderShort(props.payload, bfe);
                        const popoverDetail = getServitutMatrixColumnPopoverDetail(props.payload, bfe);
                        const columnAriaLabel = Localization.getFormattedText("{label} (BFE {bfe})", { label, bfe });
                        return (
                           <ServitutBfeColumnHeadCell
                              key={bfe}
                              bfe={bfe}
                              shortLabel={shortHeaderText}
                              popover={popoverDetail}
                              ariaLabel={columnAriaLabel}
                           />
                        );
                     })}
                     <th>{Localization.getText("Record")}</th>
                  </tr>
               </thead>
               <tbody>
                  {filteredRows.map((row) => {
                     const rowHandlingVal = row.handling ?? "legacyLlmPdf";
                     const hasPages = Boolean(row.analysisItem?.pages_kept?.length);
                     const hasSelectedPages = Boolean(row.selectedPages?.length);
                     const isTreated = props.validatedKeys.has(row.key);
                     const isReevaluating = props.reevaluatingKeys.has(row.key);
                     const aiSuggestionChanged = rowHandlingVal === "legacyLlmPdf"
                        && Boolean(row.analysisItem && !arePageSelectionsEqual(row.analysisItem.pages_kept || [], row.selectedPages || []));
                     const rowOpensViewer = row.documentSource === "rigsarkivetRequired"
                        ? false
                        : (row.handling ?? "legacyLlmPdf") === "electronicZipPackage"
                        ? Boolean(row.electronicServitut)
                        : row.pdfStatus === "ready" || row.documentSource === "annotation";
                     const rowIsExportFocus = Boolean(
                        props.exporting
                        && props.exportProgress?.stage === "document"
                        && props.exportProgress.activeRowKey === row.key,
                     );
                     const caseRelevant = getCaseRelevantDisplay(row.analysisItem?.case_relevant, hasCaseDescription);
                     const reviewTriage = getReviewTriage(row);
                     return (
                        <tr
                           key={row.key}
                           className={`servitut-redegoerelse__row servitut-redegoerelse__row--review-${reviewTriage.level} ${rowOpensViewer ? "servitut-redegoerelse__row--clickable" : ""} ${isTreated ? "servitut-redegoerelse__row--validated" : ""}${rowIsExportFocus ? " servitut-redegoerelse__row--exportActive" : ""}`}
                           onClick={() => {
                              if (rowOpensViewer) {
                                 props.onOpenRow(row);
                              }
                           }}
                        >
                           <td className="servitut-redegoerelse__treatedCell" onClick={(e) => e.stopPropagation()}>
                              <label className="servitut-redegoerelse__check servitut-redegoerelse__check--withSpinner" title={isTreated ? Localization.getText("Treated") : Localization.getText("Needs processing")} aria-label={isTreated ? Localization.getText("Treated") : Localization.getText("Needs processing")}>
                                 <input
                                    type="checkbox"
                                    checked={isTreated}
                                    disabled={props.exporting}
                                    onChange={() => props.onToggleValidated(row.key)}
                                 />
                                 {rowIsExportFocus ? (
                                    <Spinner animation="border" role="status" size="sm" className="servitut-redegoerelse__rowExportSpinner" />
                                 ) : null}
                              </label>
                           </td>
                           <td className="servitut-redegoerelse__aktCell">
                              <ServitutRedegoerelseTextClamp title={row.aktDocument || formatPdfStatusLabel(row.pdfStatus)}>
                                 <div className="servitut-redegoerelse__aktIdLine">
                                    <div className="servitut-redegoerelse__aktId">{row.datoLoebenr || row.displayAktId}</div>
                                 </div>
                                 <div className="servitut-redegoerelse__muted">
                                    <span>{row.aktDocument || formatPdfStatusLabel(row.pdfStatus)}</span>
                                    {row.pdfSource === "ovrigAkt" ? (
                                       <span
                                          className="servitut-redegoerelse__sourceHint servitut-redegoerelse__sourceHint--ovrig"
                                          title={getAktSourceText(row.pdfSource)}
                                          aria-label={getAktSourceText(row.pdfSource)}
                                       >
                                          !
                                       </span>
                                    ) : row.documentSource === "annotation" ? (
                                       <span
                                          className="servitut-redegoerelse__sourceHint servitut-redegoerelse__sourceHint--annotation"
                                          title={row.sourceMessage || Localization.getText("Using saved annotation PDF")}
                                          aria-label={row.sourceMessage || Localization.getText("Using saved annotation PDF")}
                                       >
                                          A
                                       </span>
                                    ) : row.documentSource === "rigsarkivetRequired" ? (
                                       <span
                                          className="servitut-redegoerelse__sourceHint servitut-redegoerelse__sourceHint--rigsarkiv"
                                          title={row.sourceMessage || Localization.getText("Rigsarkivet")}
                                          aria-label={row.sourceMessage || Localization.getText("Rigsarkivet")}
                                       >
                                          R
                                       </span>
                                    ) : null}
                                 </div>
                              </ServitutRedegoerelseTextClamp>
                           </td>
                           <td><span className="servitut-redegoerelse__priority">{row.priority || "-"}</span></td>
                           <td>
                              <ServitutRedegoerelseTextClamp>
                              {props.openingKey === row.key || isReevaluating ? (
                                 <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--muted">{Localization.getText("Loading...")}</span>
                              ) : row.documentSource === "rigsarkivetRequired" ? (
                                 <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--warning">
                                    {Localization.getText("Rigsarkivet")}
                                 </span>
                              ) : rowHandlingVal === "electronicZipPackage" ? (
                                 row.analysisItem ? (
                                    <div className="servitut-redegoerelse__aiCell">
                                       <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--success">
                                          {Localization.getText("Analyzed")}
                                       </span>
                                       <span className="servitut-redegoerelse__confidence">
                                          {Localization.getFormattedText("Confidence {percent}", { percent: formatConfidence(row.analysisItem.confidence) })}
                                       </span>
                                    </div>
                                 ) : props.analyzing && row.pdfStatus === "ready" ? (
                                    <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--pending">
                                       <Spinner size="sm" />
                                       <span>{Localization.getText("AI analyzing…")}</span>
                                    </span>
                                 ) : row.pdfStatus === "ready" ? (
                                    <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--muted">{Localization.getText("No analysis")}</span>
                                 ) : (
                                    <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--muted">{Localization.getText("Not available for download")}</span>
                                 )
                              ) : row.analysisItem ? (
                                 <div className={`servitut-redegoerelse__aiCell ${aiSuggestionChanged ? "servitut-redegoerelse__aiCell--overridden" : ""}`}>
                                    <span className={`servitut-redegoerelse__badge ${hasPages ? "servitut-redegoerelse__badge--success" : "servitut-redegoerelse__badge--error"}`}>
                                       {hasPages ? Localization.getFormattedText("Page {pages}", { pages: formatPageRanges(row.analysisItem.pages_kept) }) : Localization.getText("Not found")}
                                    </span>
                                    <span className="servitut-redegoerelse__confidence">{Localization.getFormattedText("Confidence {percent}", { percent: formatConfidence(row.analysisItem.confidence) })}</span>
                                 </div>
                              ) : props.analyzing && rowHandlingVal === "legacyLlmPdf" ? (
                                 <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--pending">
                                    <Spinner size="sm" />
                                    <span>{Localization.getText("AI analyzing…")}</span>
                                 </span>
                              ) : (
                                 <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--muted">{Localization.getText("No analysis")}</span>
                              )}
                              </ServitutRedegoerelseTextClamp>
                           </td>
                           <td className="servitut-redegoerelse__confidenceCell">
                              <ServitutRedegoerelseTextClamp
                                 className="servitut-redegoerelse__aiCell"
                                 title={reviewTriage.title}
                              >
                                 <span className={`servitut-redegoerelse__reviewBadge servitut-redegoerelse__reviewBadge--${reviewTriage.level}`}>
                                    <span className="servitut-redegoerelse__reviewDot" />
                                    <span>{reviewTriage.label}</span>
                                 </span>
                                 {reviewTriage.detail ? (
                                    <span className="servitut-redegoerelse__confidence">{reviewTriage.detail}</span>
                                 ) : null}
                              </ServitutRedegoerelseTextClamp>
                           </td>
                           <td>
                              {rowHandlingVal === "electronicZipPackage" ? (
                                 row.pdfStatus === "ready" ? (
                                    <ServitutRedegoerelseTextClamp className="servitut-redegoerelse__documentPackageBadges">
                                       <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--success">
                                          {Localization.getText("Full document")}
                                       </span>
                                       <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--success">
                                          SERVIMAP
                                       </span>
                                       <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--success">
                                          {Localization.getText("Attachments")}
                                       </span>
                                    </ServitutRedegoerelseTextClamp>
                                 ) : (
                                    <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--warning">—</span>
                                 )
                              ) : row.documentSource === "rigsarkivetRequired" ? (
                                 <span
                                    className="servitut-redegoerelse__badge servitut-redegoerelse__badge--warning"
                                    title={row.sourceMessage}
                                 >
                                    {Localization.getText("Rigsarkivet")}
                                 </span>
                              ) : (
                                 <span className={`servitut-redegoerelse__badge ${hasSelectedPages ? "servitut-redegoerelse__badge--success" : "servitut-redegoerelse__badge--warning"} ${aiSuggestionChanged ? "servitut-redegoerelse__badge--manual" : ""}`}>
                                    {hasSelectedPages ? Localization.getFormattedText("Page {pages}", { pages: formatPageRanges(row.selectedPages || []) }) : Localization.getText("No pages")}
                                 </span>
                              )}
                           </td>
                           <td className="servitut-redegoerelse__detailCell">
                              {row.analysisItem?.servitut_type ? (
                                 <span className="servitut-redegoerelse__badge">
                                    {formatServitutType(row.analysisItem.servitut_type)}
                                 </span>
                              ) : (
                                 <span className="servitut-redegoerelse__mutedText">-</span>
                              )}
                           </td>
                           <td className="servitut-redegoerelse__detailCell">
                              <ServitutRedegoerelseTextClamp title={row.analysisItem?.paataleberettiget || undefined}>
                                 {row.analysisItem?.paataleberettiget || "-"}
                              </ServitutRedegoerelseTextClamp>
                           </td>
                           <td className="servitut-redegoerelse__detailCell">
                              <ServitutRedegoerelseTextClamp
                                 className="servitut-redegoerelse__aiCell"
                                 title={row.analysisItem?.case_relevance_reasoning || undefined}
                              >
                                 <span className={`servitut-redegoerelse__badge ${caseRelevant.badgeClass}`}>
                                    {caseRelevant.label}
                                 </span>
                                 {row.analysisItem?.case_relevance_reasoning ? (
                                    <span className="servitut-redegoerelse__confidence">{row.analysisItem.case_relevance_reasoning}</span>
                                 ) : null}
                              </ServitutRedegoerelseTextClamp>
                           </td>
                           {props.payload.matrix.columns.map((bfe) => (
                              <td key={`${row.key}-${bfe}`} className="servitut-redegoerelse__bfeCell">
                                 {row.includedBfes.includes(bfe) ? <span className="servitut-redegoerelse__included">✓</span> : ""}
                              </td>
                           ))}
                           <td className="servitut-redegoerelse__summaryCell">
                              <ServitutRedegoerelseTextClamp
                                 title={[row.summary, row.analysisItem?.summary].filter(Boolean).join("\n\n") || undefined}
                              >
                                 <div className="servitut-redegoerelse__summary">{row.summary || Localization.getText("No text")}</div>
                                 {row.analysisItem?.summary ? (
                                    <div className="servitut-redegoerelse__aiSummary">
                                       <span className="servitut-redegoerelse__aiIcon">AI</span>
                                       <span>{row.analysisItem.summary}</span>
                                    </div>
                                 ) : null}
                              </ServitutRedegoerelseTextClamp>
                           </td>
                        </tr>
                     );
                  })}
               </tbody>
            </table>
            {filteredRows.length === 0 ? <div className="servitut-redegoerelse__empty">{Localization.getText("No rows match the filter.")}</div> : null}
         </div>
      </div>
   );
}

function MetricCard(props: { label: string; value: string | number; tone?: "success" | "accent" | "error"; title?: string; compactText?: boolean }) {
   return (
      <div
         className={`servitut-redegoerelse__metric ${props.tone ? `servitut-redegoerelse__metric--${props.tone}` : ""}`}
         title={props.title}
      >
         <div className={`servitut-redegoerelse__metricValue ${props.compactText ? "servitut-redegoerelse__metricValue--compactText" : ""}`}>
            {props.value}
         </div>
         <div className="servitut-redegoerelse__metricLabel">{props.label}</div>
      </div>
   );
}

function getSortedJordstykkerFromBfe(bfeData?: ServitutRapportBfeData): any[] {
   const sfe = bfeData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.find((node: any) => node.status === "Gældende")
      || bfeData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
   return (sfe?.jordstykkeSamlesISamletFastEjendom?.nodes || [])
      .filter((jordstykke: any) => jordstykke.status === "Gældende")
      .filter((jordstykke: any) => !sfe?.id_lokalId || jordstykke.samletFastEjendomLokalId === sfe.id_lokalId)
      .sort((a: any, b: any) => {
         const ejerlavDiff = parseInt(a.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10)
            - parseInt(b.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10);
         if (ejerlavDiff !== 0) {
            return ejerlavDiff;
         }
         return PropertyInformation.matrikelSortFunc(a.matrikelnummer || "", b.matrikelnummer || "");
      });
}

function buildServitutMatrixColumnDetail(bfeData?: ServitutRapportBfeData): ServitutMatrixColumnDetail | null {
   const jordstykker = getSortedJordstykkerFromBfe(bfeData);
   const matrikler = jordstykker.map((j: any) => j.matrikelnummer).filter(Boolean);
   if (!matrikler.length) {
      return null;
   }
   const ejerlavNames = new Set<string>();
   jordstykker.forEach((j: any) => {
      const navn = j.jordstykkeLiggerIEjerlav?.ejerlavsnavn?.trim();
      if (navn) {
         ejerlavNames.add(navn);
      }
   });
   const ejerlav = ejerlavNames.size ? Array.from(ejerlavNames).join(", ") : undefined;
   return { matrikler, ejerlav };
}

function formatServitutMatrixPropertyColumn(bfeData?: ServitutRapportBfeData): string {
   const detail = buildServitutMatrixColumnDetail(bfeData);
   if (!detail?.matrikler.length) {
      return "";
   }
   return [
      `${detail.matrikler[0]}${detail.matrikler.length > 1 ? " m.fl." : ""}`,
      detail.ejerlav,
   ].filter(Boolean).join(", ");
}

function buildServitutMatrixColumnDetails(
   data: ServitutRapportBfeData[],
   colorList: SFEColorEntry[]
): Record<string, ServitutMatrixColumnDetail> {
   const dataByBfe = new Map(data.map((entry) => [Number(entry.BFEnummer), entry]));
   return Object.fromEntries(
      colorList.map((entry) => {
         const detail = buildServitutMatrixColumnDetail(dataByBfe.get(entry.sfe));
         return [
            String(entry.sfe),
            detail ?? { matrikler: [`${entry.sfe}`] },
         ] as const;
      }),
   );
}

function buildServitutMatrixColumnLabels(
   data: ServitutRapportBfeData[],
   colorList: SFEColorEntry[]
): Record<string, string> {
   const dataByBfe = new Map(data.map((entry) => [Number(entry.BFEnummer), entry]));
   return Object.fromEntries(
      colorList.map((entry) => [
         entry.sfe,
         formatServitutMatrixPropertyColumn(dataByBfe.get(entry.sfe)) || `${entry.sfe}`,
      ])
   );
}

function getServitutMatrixColumnLabel(payload: ServitutRedegoerelsePayload, bfe: number): string {
   return payload.matrix.columnLabels?.[bfe] ?? payload.matrix.columnLabels?.[String(bfe)] ?? `${bfe}`;
}

function getServitutMatrixColumnLabelParts(label: string): { matrikel: string; ejerlav?: string } {
   const [matrikel, ...ejerlavParts] = label.split(", ");
   return {
      matrikel: matrikel || label,
      ejerlav: ejerlavParts.join(", ") || undefined,
   };
}

function getServitutMatrixColumnHeaderShort(payload: ServitutRedegoerelsePayload, bfe: number): string {
   const detail = payload.matrix.columnDetails?.[String(bfe)];
   if (detail?.matrikler?.length) {
      return `${detail.matrikler[0]}${detail.matrikler.length > 1 ? " m.fl." : ""}`;
   }
   const label = getServitutMatrixColumnLabel(payload, bfe);
   const parts = getServitutMatrixColumnLabelParts(label);
   return parts.matrikel || `${bfe}`;
}

function getServitutMatrixColumnPopoverDetail(
   payload: ServitutRedegoerelsePayload,
   bfe: number
): ServitutMatrixColumnDetail {
   const fromPayload = payload.matrix.columnDetails?.[String(bfe)];
   if (fromPayload?.matrikler?.length) {
      return fromPayload;
   }
   const label = getServitutMatrixColumnLabel(payload, bfe);
   const parts = getServitutMatrixColumnLabelParts(label);
   return {
      matrikler: parts.matrikel ? [parts.matrikel] : [`${bfe}`],
      ejerlav: parts.ejerlav,
   };
}

function hasPayloadAnalysis(payload: ServitutRedegoerelsePayload | null): boolean {
   return Boolean(payload?.matrix.rows.some((row) => row.analysisItem));
}

function applyCaseDescriptionToPayload(
   payload: ServitutRedegoerelsePayload,
   caseDescription: string
): ServitutRedegoerelsePayload {
   const normalizedCaseDescription = caseDescription.trim();
   return {
      ...payload,
      caseDescription: normalizedCaseDescription || undefined,
      metadata: payload.metadata.map((metadata) => {
         if (!normalizedCaseDescription) {
            const rest = { ...metadata };
            delete rest.caseDescription;
            return rest;
         }
         return {
            ...metadata,
            caseDescription: normalizedCaseDescription,
         };
      }),
   };
}

function hasAnalyzableServitutRows(payload: ServitutRedegoerelsePayload | null): boolean {
   if (!payload) {
      return false;
   }
   return payload.metadata.length > 0 || payload.matrix.rows.some((row) => (
      row.documentSource === "annotation" &&
      row.pdfStatus === "ready" &&
      Boolean(row.annotationDocument?.s3Key)
   ));
}

function isAbortError(error: unknown): boolean {
   return error instanceof DOMException && error.name === "AbortError";
}

function buildRowEvaluationMetadata(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   payload?: ServitutRedegoerelsePayload | null,
   caseDescription?: string,
): LLMMetadata {
   const fromPayload = payload?.metadata.find((metadata) => metadata.id === row.key);
   const normalizedCaseDescription = caseDescription?.trim();
   const metadata: LLMMetadata = fromPayload ? { ...fromPayload } : {
      id: row.key,
      document: row.annotationDocument?.filename || row.aktDocument || row.exportFileName || `${row.displayAktId || row.key}.pdf`,
      documentUrl: row.documentUrl || null,
      aktId: row.aktId || row.analysisAktId || row.key,
      tekster: [...getServitutTekster(row.servitut), row.summary].filter(Boolean),
      fromOvrigDataSource: row.pdfSource === "ovrigAkt",
   };
   return {
      ...metadata,
      id: row.key,
      caseDescription: normalizedCaseDescription || metadata.caseDescription || null,
   };
}

async function analyzeAnnotationRows(
   payload: ServitutRedegoerelsePayload,
   analyzer: ReturnType<typeof createANNLLMAnalyzer>,
   signal: AbortSignal,
   onResult: (result: LLMAnalysisResultItem) => void,
   onFailure: () => void,
) {
   const rows = payload.matrix.rows.filter((row) => (
      row.documentSource === "annotation" &&
      row.pdfStatus === "ready" &&
      Boolean(row.annotationDocument?.s3Key)
   ));
   await runWithConcurrency(rows, Math.min(3, SERVITUT_ANALYSIS_CONCURRENCY), async (row) => {
      if (signal.aborted) return;
      try {
         const { blob, fileName } = await downloadOverlayImage(row.annotationDocument!.s3Key);
         if (signal.aborted) return;
         const result = await analyzer.evaluateServitutDocument({
            metadata: buildRowEvaluationMetadata(row, payload, payload.caseDescription),
            pages: row.selectedPages,
            pdfBlob: blob,
            documentFileName: row.annotationDocument?.filename || fileName || row.exportFileName || "servitut.pdf",
            signal,
         });
         onResult({ ...result, id: row.key });
      } catch (error) {
         if (isAbortError(error) || signal.aborted) return;
         onFailure();
         console.error("[Servitutredegørelse] AI annotation row failed", row.key, error);
      }
   }, signal);
}

async function getRowSourcePdfBlob(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): Promise<Blob | null> {
   if (row.documentSource === "annotation") {
      if (!row.annotationDocument?.s3Key) {
         return null;
      }
      const { blob } = await downloadOverlayImage(row.annotationDocument.s3Key);
      return blob;
   }
   if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
      const target = row.electronicServitut ? getTingbogDownloadTarget(row.electronicServitut) : undefined;
      return target ? await fetchTingbogDocumentBlob(target) : null;
   }
   if (!row.aktId) {
      return null;
   }
   return await getANNLLMAnalyzer().fetchPdf(row.aktId);
}

function canSaveServitutRowAsAnnotation(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): boolean {
   if (row.annotationRecord || row.documentSource === "annotation" || row.documentSource === "rigsarkivetRequired") {
      return false;
   }
   if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
      return row.pdfStatus === "ready" && Boolean(row.electronicServitut);
   }
   return row.pdfStatus === "ready" && Boolean(row.aktId);
}

async function prepareServitutRedegoerelsePayload(
   data: ServitutRapportBfeData[],
   colorList: SFEColorEntry[],
   options: { organizationId?: string } = {},
): Promise<ServitutRedegoerelsePayload> {
   const rows = buildServitutMatrixRows(data);
   const filesByDocument = new Map<string, ServitutRedegoerelsePreparedAkt>();
   const metadata: LLMMetadata[] = [];
   const analyzer = getANNLLMAnalyzer();

   for (const row of rows) {
      const firstBfe = data.find((bfeData) => row.includedBfes.has(Number(bfeData.BFEnummer)));
      const existingAnnotation = await findExistingServitutPdfAnnotation(row, options.organizationId);
      if (existingAnnotation?.document?.s3Key) {
         row.documentSource = "annotation";
         row.annotationRecord = existingAnnotation;
         row.annotationDocument = existingAnnotation.document;
         row.pdfStatus = "ready";
         row.aktDocument = existingAnnotation.document.filename || row.exportFileName || Localization.getText("Saved annotation PDF");
         row.sourceMessage = Localization.getText("Using saved annotation PDF");
         continue;
      }

      if (isBeforeRigsarkivetCutoff(row.servitut, row.datoLoebenr)) {
         row.documentSource = "rigsarkivetRequired";
         row.pdfStatus = "missing";
         row.aktDocument = Localization.getText("Rigsarkivet");
         row.sourceMessage = Localization.getText("Servitutter before 1 April 1927 are not expected in Tingbogen. Look in Rigsarkivet or upload the document as an annotation.");
         continue;
      }

      row.documentSource = "tingbog";
      if (row.handling === "electronicZipPackage") {
         if (!isEligibleDigitalServitut(row.servitut)) {
            row.pdfStatus = "missing";
         } else {
            row.pdfStatus = "ready";
            row.aktDocument = Localization.getText("Digital land registry PDF");
            const xmlText = await fetchDigitalServitutXml(row.servitut).catch((error) => {
               console.error("[Servitutredegørelse] failed to fetch digital servitut XML", row.key, error);
               return null;
            });
            if (xmlText) {
               const entryMetadata: LLMMetadata = {
                  id: row.key,
                  document: xmlText,
                  documentUrl: null,
                  aktId: null,
                  tekster: getServitutTekster(row.servitut),
                  fromOvrigDataSource: null,
               };
               metadata.push(entryMetadata);
            }
         }
         continue;
      }

      const fetchAktId = getServitutPdfFetchAktId(row.servitut, firstBfe?.stamOplysninger);
      if (!fetchAktId) {
         row.pdfStatus = "missing";
         continue;
      }

      try {
         const includedBfeData = Array.from(row.includedBfes)
            .map((bfe) => data.find((bfeData) => Number(bfeData.BFEnummer) === bfe))
            .filter((entry): entry is ServitutRapportBfeData => Boolean(entry));
         const builtMetadata = analyzer.buildMetadata(
            row.servitut,
            firstBfe?.stamOplysninger,
            undefined,
            undefined,
            firstBfe ?? includedBfeData[0],
            { propertyDataList: includedBfeData }
         );
         const fromOvrigDataSource = isServitutFromOvrigAktSource(row.servitut, firstBfe?.stamOplysninger);
         const pdfSource: ServitutRedegoerelsePreparedAkt["source"] =
            fromOvrigDataSource ? "ovrigAkt" : "servitutAkt";

         let file = filesByDocument.get(fetchAktId);
         if (!file) {
            file = {
               document: builtMetadata.document,
               aktId: fetchAktId,
               documentUrl: await analyzer.getAktFileUrl(fetchAktId),
               source: pdfSource,
               servitutKeys: [],
            };
            filesByDocument.set(fetchAktId, file);
         }
         const entryMetadata: LLMMetadata = {
            ...builtMetadata,
            id: row.key,
            documentUrl: file.documentUrl,
            fromOvrigDataSource,
         };
         file.servitutKeys.push(row.key);
         metadata.push(entryMetadata);
         row.pdfStatus = "ready";
         row.pdfSource = pdfSource;
         row.aktDocument = builtMetadata.document;
         row.aktId = fetchAktId;
         row.documentUrl = file.documentUrl;
      } catch (error) {
         row.pdfStatus = "failed";
         console.error("[Servitutredegørelse] failed to prepare akt", row.key, error);
      }
   }

   const electronicReadyRows = rows.filter((r) => r.handling === "electronicZipPackage" && r.pdfStatus === "ready").length;
   const annotationReadyRows = rows.filter((r) => r.documentSource === "annotation" && r.pdfStatus === "ready").length;

   return {
      metadata,
      matrix: {
         columns: colorList.map((entry) => entry.sfe),
         columnLabels: buildServitutMatrixColumnLabels(data, colorList),
         columnDetails: buildServitutMatrixColumnDetails(data, colorList),
         uniqueAktCount: filesByDocument.size + electronicReadyRows + annotationReadyRows,
         rows: rows.map((row) => ({
            key: row.key,
            servitut: row.servitut,
            handling: row.handling,
            electronicServitut:
               row.handling === "electronicZipPackage" && row.pdfStatus === "ready"
                  ? ({ ...row.servitut } as ServitutElectronicPackageServitut)
                  : undefined,
            displayAktId: row.displayAktId,
            datoLoebenr: row.datoLoebenr,
            priority: row.priority,
            summary: row.summary,
            analysisAktId: row.analysisAktId,
            includedBfes: Array.from(row.includedBfes),
            pdfStatus: row.pdfStatus,
            pdfSource: row.pdfSource,
            aktDocument: row.aktDocument,
            aktId: row.aktId,
            documentUrl: row.documentUrl,
            analysisItem: row.analysisItem,
            selectedPages: row.selectedPages,
            exportFileName: row.exportFileName,
            documentSource: row.documentSource,
            annotationRecord: row.annotationRecord,
            annotationDocument: row.annotationDocument,
            sourceMessage: row.sourceMessage,
         })),
      },
   };
}

function buildServitutMatrixRows(data: ServitutRapportBfeData[]): ServitutMatrixRow[] {
   const rowsByKey = new Map<string, ServitutMatrixRow>();
   data.forEach((bfeData) => {
      const bfe = Number(bfeData.BFEnummer);
      (bfeData.servitutter || []).forEach((servitut) => {
         const handling: ServitutRedegoerelseRowHandling = isTingbogElectronicDocument(servitut)
            ? "electronicZipPackage"
            : "legacyLlmPdf";
         const key = getServitutKey(servitut);
         const existing = rowsByKey.get(key);
         if (existing) {
            existing.includedBfes.add(bfe);
            return;
         }
         rowsByKey.set(key, {
            key,
            servitut,
            handling,
            displayAktId: aktIdFormat(servitut.aktId || servitut.aktAliasId || key),
            datoLoebenr: getServitutDatoLoebenr(servitut, key),
            priority: `${servitut.prioritetNummer || ""}`,
            summary: getServitutSummary(servitut),
            analysisAktId: servitut?.aktId || servitut?.aktAliasId || key,
            includedBfes: new Set([bfe]),
            pdfStatus: "pending",
            exportFileName: getServitutMatrixFileName(servitut),
         });
      });
   });
   return Array.from(rowsByKey.values()).sort((a, b) => {
      const bfeDiff = Math.min(...Array.from(a.includedBfes)) - Math.min(...Array.from(b.includedBfes));
      if (bfeDiff !== 0) return bfeDiff;
      return comparePriorityThenDato(a, b);
   });
}

function getServitutKey(servitut: any): string {
   const identity = normalizeServitutIdentity(servitut);
   return identity.documentId || identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || getServitutSummary(servitut);
}

const RIGSARKIVET_TINGBOG_CUTOFF_MS = Date.UTC(1927, 3, 1, 0, 0, 0, 0);

function parseDatoLoebenrToUtcMs(value?: string): number | null {
   const match = /^(\d{2})(\d{2})(\d{4})-/.exec(value?.trim() || "");
   if (!match) {
      return null;
   }
   const day = Number(match[1]);
   const month = Number(match[2]) - 1;
   const year = Number(match[3]);
   const ms = Date.UTC(year, month, day, 0, 0, 0, 0);
   return Number.isNaN(ms) ? null : ms;
}

function isBeforeRigsarkivetCutoff(servitut: any, datoLoebenr?: string): boolean {
   const tinglysningsDatoMs = parseTinglysningsDatoToUtcMs(servitut?.tinglysningsDato);
   const datoloebenrMs = parseDatoLoebenrToUtcMs(normalizeServitutIdentity(servitut).datoloebenr)
      ?? parseDatoLoebenrToUtcMs(datoLoebenr);
   const ms = tinglysningsDatoMs ?? datoloebenrMs;
   return ms !== null && ms < RIGSARKIVET_TINGBOG_CUTOFF_MS;
}

function getCanonicalServitutAnnotationDocumentId(row: { servitut?: any; key: string }): string {
   const identity = normalizeServitutIdentity(row.servitut || {});
   return getServitutAnnotationDocumentId(row.servitut || {}) || identity.documentId || row.key;
}

async function findExistingServitutPdfAnnotation(
   row: ServitutMatrixRow,
   organizationId?: string,
): Promise<ANNAnnotationRecord | null> {
   const documentId = getCanonicalServitutAnnotationDocumentId(row);
   if (!documentId) {
      return null;
   }
   try {
      const annotations = await ANNManager.listAnnotations(
         AnnotationContextType.Servitut,
         documentId,
         organizationId,
      );
      return findServitutDocumentAnnotation(annotations, row.datoLoebenr);
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to look up servitut annotations", row.key, error);
      return null;
   }
}

function getServitutSummary(servitut: any): string {
   return (servitut?.tekstSummarisk || (Array.isArray(servitut?.tekster) ? servitut.tekster.map((t: any) => t?.tekst ?? t).join(", ") : "") || "").trim();
}

function getServitutDatoLoebenr(servitut: any, fallback: string): string {
   const identity = normalizeServitutIdentity(servitut);
   return identity.datoloebenr || aktIdFormat(identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || fallback);
}

function getServitutMatrixFileName(servitut: any): string {
   const identity = normalizeServitutIdentity(servitut);
   return getServitutDownloadFileName({
      datoloebenr: identity.datoloebenr,
      priorityNumber: servitut?.prioritetNummer,
      headingText: getServitutSummary(servitut),
      fallbackStem: identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || "servitut",
   });
}

function getServitutTekster(servitut: any): string[] {
   const tekster: string[] = [];
   if (Array.isArray(servitut?.tekster)) {
      tekster.push(...servitut.tekster.map((t: any) => t?.tekst ?? t).filter(Boolean));
   }
   if (servitut?.tekstSummarisk) {
      tekster.push(servitut.tekstSummarisk);
   }
   return tekster;
}

async function fetchDigitalServitutXml(servitut: any): Promise<string | null> {
   const dokumentId = servitut?.dokumentId?.trim();
   if (!dokumentId) {
      return null;
   }
   const xmlDoc = await DC.TL.DokumentTingbog(dokumentId);
   return new XMLSerializer().serializeToString(xmlDoc);
}

function formatConfidence(confidence?: number): string {
   if (confidence === undefined || confidence === null) return "-";
   const numeric = normalizeConfidenceValue(confidence);
   if (numeric === null) return "-";
   return `${Math.round(numeric * 100)}%`;
}

function formatPdfStatusLabel(status: ServitutMatrixRow["pdfStatus"]): string {
   switch (status) {
      case "pending":
         return Localization.getText("PDF pending");
      case "ready":
         return Localization.getText("PDF ready");
      case "missing":
         return Localization.getText("PDF missing");
      case "failed":
         return Localization.getText("PDF failed");
      default:
         return status;
   }
}

function formatServitutType(value?: string): string {
   if (!value) return "-";
   const normalized = value.toLowerCase().replace(/\s+/g, "");
   if (normalized === "rådighedsservitut") return Localization.getText("Easement of use");
   if (normalized === "tilstandsservitut") return Localization.getText("Condition easement");
   return value;
}

function formatCaseRelevant(value?: boolean): string {
   if (value === true) return Localization.getText("Yes");
   if (value === false) return Localization.getText("No");
   return "";
}

function getCaseRelevantDisplay(value: boolean | undefined, hasCaseDescription: boolean): { label: string; badgeClass: string } {
   if (!hasCaseDescription) {
      return { label: Localization.getText("No case"), badgeClass: "servitut-redegoerelse__badge--muted" };
   }
   if (value === true) {
      return { label: Localization.getText("Yes"), badgeClass: "servitut-redegoerelse__badge--success" };
   }
   if (value === false) {
      return { label: Localization.getText("No"), badgeClass: "servitut-redegoerelse__badge--warning" };
   }
   return { label: Localization.getText("Not assessed"), badgeClass: "servitut-redegoerelse__badge--muted" };
}

type ReviewTriageLevel = "good" | "medium" | "bad" | "pending" | "unknown";

function normalizeConfidenceValue(value?: number): number | null {
   if (value === undefined || value === null) {
      return null;
   }
   if (!Number.isFinite(value)) {
      return null;
   }
   // Older cached/dev results used 1-10. Normalize defensively while backend now returns 0-1.
   return value > 1 ? value / 10 : value;
}

function getReviewTriage(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): {
   level: ReviewTriageLevel;
   label: string;
   detail: string;
   title: string;
} {
   const item = row.analysisItem;
   if (!item) {
      return {
         level: "pending",
         label: Localization.getText("Waiting"),
         detail: "",
         title: Localization.getText("AI has not yet returned overall confidence for this row."),
      };
   }

   const confidence = normalizeConfidenceValue(item.overall_confidence ?? item.confidence);
   const percent = confidence === null ? "" : `${Math.round(confidence * 100)}%`;
   const reviewReasons = item.review_reasons?.filter(Boolean) || [];
   const detailParts = [
      item.overall_confidence_reasoning,
      ...reviewReasons,
   ].filter(Boolean);
   const detail = detailParts[0] || "";
   const title = detailParts.length ? detailParts.join("\n") : item.reasoning || Localization.getText("No further rationale.");

   if (item.needs_review === true) {
      return {
         level: "bad",
         label: percent ? Localization.getFormattedText("Review {percent}", { percent }) : Localization.getText("Review required"),
         detail,
         title,
      };
   }
   if (confidence === null) {
      return {
         level: "unknown",
         label: Localization.getText("Unknown"),
         detail,
         title,
      };
   }
   if (confidence >= 0.85) {
      return {
         level: "good",
         label: Localization.getFormattedText("High {percent}", { percent }),
         detail,
         title,
      };
   }
   if (confidence >= 0.6) {
      return {
         level: "medium",
         label: Localization.getFormattedText("Medium {percent}", { percent }),
         detail,
         title,
      };
   }
   return {
      level: "bad",
      label: Localization.getFormattedText("Low {percent}", { percent }),
      detail,
      title,
   };
}

function formatPageRanges(pages: number[]): string {
   const sortedPages = normalizePages(pages);
   if (!sortedPages.length) return "-";

   const ranges: string[] = [];
   let rangeStart = sortedPages[0];
   let previousPage = sortedPages[0];
   for (let index = 1; index < sortedPages.length; index += 1) {
      const page = sortedPages[index];
      if (page === previousPage + 1) {
         previousPage = page;
         continue;
      }
      ranges.push(rangeStart === previousPage ? `${rangeStart}` : `${rangeStart}-${previousPage}`);
      rangeStart = page;
      previousPage = page;
   }
   ranges.push(rangeStart === previousPage ? `${rangeStart}` : `${rangeStart}-${previousPage}`);
   return ranges.join(", ");
}

function normalizePages(pages: number[]): number[] {
   return Array.from(new Set(pages))
      .filter((page) => Number.isFinite(page) && page > 0)
      .sort((a, b) => a - b);
}

function arePageSelectionsEqual(a: number[], b: number[]): boolean {
   const normalizedA = normalizePages(a);
   const normalizedB = normalizePages(b);
   return normalizedA.length === normalizedB.length && normalizedA.every((page, index) => page === normalizedB[index]);
}

function getAktSourceText(source?: ServitutRedegoerelsePreparedAkt["source"]): string {
   if (source === "ovrigAkt") return Localization.getText("Akt id was resolved via other deeds (øvrige akter).");
   if (source === "servitutAkt") return Localization.getText("Akt id comes directly from the easement.");
   return Localization.getText("Akt source is not determined.");
}

function sortServitutRows<T extends { includedBfes: number[]; priority: string; datoLoebenr?: string; displayAktId: string }>(
   rows: T[],
   columns: number[],
   datoSort: "default" | "asc" | "desc"
): T[] {
   return [...rows].sort((a, b) => {
      if (datoSort !== "default") {
         const dateDiff = compareDatoLoebenr(a, b);
         return datoSort === "asc" ? dateDiff : -dateDiff;
      }

      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return comparePriorityThenDato(a, b);
   });
}

function sortRowsByReviewConfidence<T extends ServitutRedegoerelsePayload["matrix"]["rows"][number]>(
   rows: T[],
   direction: "asc" | "desc"
): T[] {
   return [...rows].sort((a, b) => {
      const diff = getReviewConfidenceSortValue(a) - getReviewConfidenceSortValue(b);
      return direction === "asc" ? diff : -diff;
   });
}

type ServitutMatrixSortColumn =
   | "dato"
   | "confidence"
   | "priority"
   | "aiSuggestion"
   | "pagesKept"
   | "servitutType"
   | "entitledParty"
   | "caseRelevant";

export type ServitutMatrixSortState =
   | { column: ServitutMatrixSortColumn; direction: "asc" | "desc" }
   | null;

function getAiSuggestionSortValue(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): number {
   const handling = row.handling ?? "legacyLlmPdf";
   const ai = row.analysisItem;
   if (!ai) return -2;
   const conf = normalizeConfidenceValue(ai.confidence);
   const c = conf ?? -1;
   if (handling === "electronicZipPackage") {
      return c;
   }
   const pageCount = ai.pages_kept?.length ?? 0;
   return pageCount > 0 ? 10 + c : c - 0.01;
}

function getPagesKeptSortValue(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): number {
   const handling = row.handling ?? "legacyLlmPdf";
   if (handling === "electronicZipPackage") {
      return row.pdfStatus === "ready" ? 100000 : 0;
   }
   return normalizePages(row.selectedPages || []).length;
}

function getCaseRelevantSortRank(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   hasCaseDescription: boolean,
): number {
   if (!hasCaseDescription) return 0;
   const v = row.analysisItem?.case_relevant;
   if (v === true) return 3;
   if (v === false) return 2;
   return 1;
}

function sortServitutRowsByPriority(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const pa = parseInt(a.priority, 10);
      const pb = parseInt(b.priority, 10);
      const na = Number.isFinite(pa) ? pa : Number.MAX_SAFE_INTEGER;
      const nb = Number.isFinite(pb) ? pb : Number.MAX_SAFE_INTEGER;
      const diff = na - nb;
      if (diff !== 0) return direction === "asc" ? diff : -diff;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByAiSuggestion(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const diff = getAiSuggestionSortValue(a) - getAiSuggestionSortValue(b);
      if (diff !== 0) return direction === "asc" ? diff : -diff;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByPagesKept(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const diff = getPagesKeptSortValue(a) - getPagesKeptSortValue(b);
      if (diff !== 0) return direction === "asc" ? diff : -diff;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByServitutType(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const sa = formatServitutType(a.analysisItem?.servitut_type);
      const sb = formatServitutType(b.analysisItem?.servitut_type);
      const cmp = sa.localeCompare(sb, "da", { sensitivity: "base" });
      if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByEntitledParty(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const sa = (a.analysisItem?.paataleberettiget || "").trim();
      const sb = (b.analysisItem?.paataleberettiget || "").trim();
      const cmp = sa.localeCompare(sb, "da", { sensitivity: "base" });
      if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByCaseRelevant(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   hasCaseDescription: boolean,
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const diff = getCaseRelevantSortRank(a, hasCaseDescription) - getCaseRelevantSortRank(b, hasCaseDescription);
      if (diff !== 0) return direction === "asc" ? diff : -diff;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function applyServitutMatrixSort(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   hasCaseDescription: boolean,
   sortState: ServitutMatrixSortState,
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   if (!sortState) {
      return sortServitutRows(rows, columns, "default");
   }
   const { column, direction } = sortState;
   const baseDefault = sortServitutRows(rows, columns, "default");

   switch (column) {
      case "dato":
         return sortServitutRows(rows, columns, direction === "asc" ? "asc" : "desc");
      case "confidence":
         return sortRowsByReviewConfidence(baseDefault, direction);
      case "priority":
         return sortServitutRowsByPriority(rows, columns, direction);
      case "aiSuggestion":
         return sortServitutRowsByAiSuggestion(rows, columns, direction);
      case "pagesKept":
         return sortServitutRowsByPagesKept(rows, columns, direction);
      case "servitutType":
         return sortServitutRowsByServitutType(rows, columns, direction);
      case "entitledParty":
         return sortServitutRowsByEntitledParty(rows, columns, direction);
      case "caseRelevant":
         return sortServitutRowsByCaseRelevant(rows, columns, hasCaseDescription, direction);
      default:
         return baseDefault;
   }
}

function getReviewConfidenceSortValue(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): number {
   const item = row.analysisItem;
   if (!item) {
      return -1;
   }
   const confidence = normalizeConfidenceValue(item.overall_confidence ?? item.confidence);
   if (item.needs_review === true) {
      return -0.5 + ((confidence ?? 0) / 100);
   }
   return confidence ?? -0.25;
}

function getFirstBfeColumnIndex(includedBfes: number[], columns: number[]): number {
   const columnIndexes = includedBfes.map((bfe) => {
      const index = columns.indexOf(bfe);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
   });
   return Math.min(...columnIndexes);
}

function comparePriorityThenDato(
   a: { priority: string; datoLoebenr?: string; displayAktId: string },
   b: { priority: string; datoLoebenr?: string; displayAktId: string }
): number {
   const priorityDiff = (parseInt(a.priority) || 0) - (parseInt(b.priority) || 0);
   if (priorityDiff !== 0) return priorityDiff;
   return compareDatoLoebenr(a, b);
}

function compareDatoLoebenr(
   a: { datoLoebenr?: string; displayAktId: string },
   b: { datoLoebenr?: string; displayAktId: string }
): number {
   const aDate = parseDatoLoebenrDate(a.datoLoebenr || a.displayAktId);
   const bDate = parseDatoLoebenrDate(b.datoLoebenr || b.displayAktId);
   if (aDate !== bDate) return aDate - bDate;
   return (a.datoLoebenr || a.displayAktId).localeCompare(b.datoLoebenr || b.displayAktId);
}

function parseDatoLoebenrDate(value: string): number {
   const trimmed = value.trim();
   const danishDate = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(trimmed);
   if (danishDate) {
      return toValidUtcDate(Number(danishDate[3]), Number(danishDate[2]), Number(danishDate[1]));
   }
   const compactDanishDate = /^(\d{2})(\d{2})(\d{4})/.exec(trimmed);
   if (compactDanishDate) {
      const date = toValidUtcDate(Number(compactDanishDate[3]), Number(compactDanishDate[2]), Number(compactDanishDate[1]));
      if (date !== Number.MAX_SAFE_INTEGER) return date;
   }
   const compactIsoDate = /^(\d{4})(\d{2})(\d{2})/.exec(trimmed);
   if (compactIsoDate) {
      return toValidUtcDate(Number(compactIsoDate[1]), Number(compactIsoDate[2]), Number(compactIsoDate[3]));
   }
   return Number.MAX_SAFE_INTEGER;
}

function toValidUtcDate(year: number, month: number, day: number): number {
   if (year < 1800 || year > 2200 || month < 1 || month > 12 || day < 1 || day > 31) {
      return Number.MAX_SAFE_INTEGER;
   }
   return Date.UTC(year, month - 1, day);
}

function buildServitutMatrixExcelBlob(payload: ServitutRedegoerelsePayload, treatedKeys: Set<string>): Blob {
   const rows = buildServitutMatrixExcelRows(payload, treatedKeys);
   const worksheet = XlsxUtils.json_to_sheet(rows);
   const workbook = XlsxUtils.book_new();
   XlsxUtils.book_append_sheet(workbook, worksheet, "Servitutredegørelse");
   const buffer = writeXlsx(workbook, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
   return new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

function buildServitutMatrixExcelRows(payload: ServitutRedegoerelsePayload, treatedKeys: Set<string>) {
   const hasCaseDescription = Boolean(payload.caseDescription?.trim());
   return sortServitutRows(payload.matrix.rows, payload.matrix.columns, "default").map((row) => {
      const handling = row.handling ?? "legacyLlmPdf";
      const reviewTriage = getReviewTriage(row);
      const record: Record<string, string | number> = {
         "Dato løbenr": row.datoLoebenr || row.displayAktId,
         "Prioritet": row.priority,
         "Dokumenttype": handling === "electronicZipPackage" ? "Digital tingbog" : "Indskannet akt",
         "AI sider": handling === "electronicZipPackage"
            ? ""
            : (row.analysisItem?.pages_kept?.length ? formatPageRanges(row.analysisItem.pages_kept) : ""),
         "Gemmes sider": handling === "electronicZipPackage"
            ? "Akt + SERVIMAP + bilag (ved eksport)"
            : (row.selectedPages?.length ? formatPageRanges(row.selectedPages) : ""),
         "Behandlet": treatedKeys.has(row.key) ? "x" : "",
         "AI resume": row.analysisItem?.summary || "",
         "AI samlet sikkerhed": reviewTriage.label,
         "AI samlet sikkerhed begrundelse": row.analysisItem?.overall_confidence_reasoning || "",
         "Kræver tjek": row.analysisItem?.needs_review ? "x" : "",
         "Tjek årsager": row.analysisItem?.review_reasons?.join("; ") || "",
         "Servituttype": formatServitutType(row.analysisItem?.servitut_type),
         "Servituttype sikkerhed": formatConfidence(row.analysisItem?.servitut_type_confidence),
         "Påtaleberettiget": row.analysisItem?.paataleberettiget || "",
         "Påtaleberettiget sikkerhed": formatConfidence(row.analysisItem?.paataleberettiget_confidence),
         "Case relevant": hasCaseDescription ? formatCaseRelevant(row.analysisItem?.case_relevant) : "",
         "Case begrundelse": row.analysisItem?.case_relevance_reasoning || "",
         "Case sikkerhed": formatConfidence(row.analysisItem?.case_relevance_confidence),
         "Servitut": row.summary,
         "PDF status": row.pdfStatus,
         "PDF kilde": row.pdfSource || "",
      };
      payload.matrix.columns.forEach((bfe) => {
         record[getServitutMatrixColumnLabel(payload, bfe)] = row.includedBfes.includes(bfe) ? "x" : "";
      });
      return record;
   });
}

async function buildServitutRedegoerelseZip(
   payload: ServitutRedegoerelsePayload,
   treatedKeys: Set<string>,
   onProgress?: (state: ServitutRedegoerelseExportProgress) => void | Promise<void>,
): Promise<Blob> {
   const zip = new JSZip();
   const usedNames = new Set<string>();
   const analyzer = getANNLLMAnalyzer();
   const rows = sortServitutRows(
      payload.matrix.rows.filter((row) => treatedKeys.has(row.key)),
      payload.matrix.columns,
      "default"
   );
   const totalSteps = 2 + rows.length;

   const emit = async (state: ServitutRedegoerelseExportProgress): Promise<void> => {
      await onProgress?.(state);
   };

   let completedSteps = 0;
   await emit({
      completedSteps,
      totalSteps,
      stage: "spreadsheet",
      activeRowKey: null,
   });

   zip.file(
      createUniqueZipEntryName("Servitutredegørelse.xlsx", usedNames),
      buildServitutMatrixExcelBlob(payload, treatedKeys),
   );
   completedSteps += 1;
   await emit({
      completedSteps,
      totalSteps,
      stage: "spreadsheet",
      activeRowKey: null,
   });

   for (let docIndex = 0; docIndex < rows.length; docIndex++) {
      const row = rows[docIndex];
      await emit({
         completedSteps,
         totalSteps,
         stage: "document",
         activeRowKey: row.key,
         documentIndex: docIndex + 1,
         documentTotal: rows.length,
      });

      const handling = row.handling ?? "legacyLlmPdf";
      if (handling === "electronicZipPackage") {
         const baseFileName = row.exportFileName || `${row.displayAktId || row.key}.pdf`;
         if (!row.electronicServitut) {
            zip.file(
               createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
               `Digital servitutpakke ikke tilgængelig (${row.displayAktId}).\n${row.summary || ""}`,
            );
         } else {
            try {
               const packageResult = await addServitutPackageToZip(zip, row.electronicServitut);
               if (packageResult.warnings.length) {
                  console.warn("[Servitutredegørelse] digital pakke-advarsler", row.key, packageResult.warnings);
               }
            } catch (error) {
               zip.file(
                  createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
                  `Kunne ikke hente digital servitutpakke: ${error instanceof Error ? error.message : String(error)}\n\n${row.summary || "Ingen servituttekst."}`,
               );
            }
         }
      } else {
         const pagesToKeep = normalizePages(row.selectedPages || []);
         const baseFileName = row.exportFileName || `${row.displayAktId || row.key}.pdf`;
         if (row.documentSource === "annotation" && !pagesToKeep.length) {
            try {
               const pdfBlob = await getRowSourcePdfBlob(row);
               if (!pdfBlob) {
                  throw new Error("PDF mangler");
               }
               zip.file(createUniqueZipEntryName(baseFileName, usedNames), pdfBlob);
            } catch (error) {
               zip.file(
                  createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
                  `${buildMissingServitutText(row)}\n\nPDF kunne ikke hentes fra annotation: ${error instanceof Error ? error.message : String(error)}`,
               );
            }
         } else if (!pagesToKeep.length) {
            zip.file(
               createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
               buildMissingServitutText(row),
            );
         } else if (!row.aktId && row.documentSource !== "annotation") {
            zip.file(
               createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
               `${buildMissingServitutText(row)}\n\nPDF kunne ikke hentes, fordi akt-id mangler.`,
            );
         } else {
            try {
               const pdfBlob = row.documentSource === "annotation"
                  ? await getRowSourcePdfBlob(row)
                  : row.aktId ? await analyzer.fetchPdf(row.aktId) : null;
               if (!pdfBlob) {
                  throw new Error("PDF mangler");
               }
               const cutPdfBlob = await cutPdfToSelectedPages(pdfBlob, pagesToKeep);
               zip.file(createUniqueZipEntryName(baseFileName, usedNames), cutPdfBlob);
            } catch (error) {
               zip.file(
                  createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
                  `${buildMissingServitutText(row)}\n\nPDF kunne ikke klippes: ${error instanceof Error ? error.message : String(error)}`,
               );
            }
         }
      }

      completedSteps += 1;
   }

   await emit({
      completedSteps,
      totalSteps,
      stage: "compress",
      activeRowKey: null,
   });

   const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
   completedSteps += 1;
   await emit({
      completedSteps,
      totalSteps,
      stage: "compress",
      activeRowKey: null,
   });

   return blob;
}

function buildMissingServitutText(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): string {
   return [
      `Could not find servitut: ${row.displayAktId}`,
      `Prioritet: ${row.priority || "-"}`,
      `AI forslag: ${row.analysisItem?.pages_kept?.length ? formatPageRanges(row.analysisItem.pages_kept) : "Ingen sider"}`,
      "",
      row.summary || "Ingen servituttekst.",
   ].join("\n");
}

function toTxtFileName(fileName: string): string {
   return fileName.replace(/\.[^.]+$/i, "") + ".txt";
}

function createUniqueZipEntryName(fileName: string, usedNames: Set<string>): string {
   const trimmed = fileName.trim() || "servitut.pdf";
   if (!usedNames.has(trimmed)) {
      usedNames.add(trimmed);
      return trimmed;
   }
   const match = /^(.*?)(\.[^.]+)?$/.exec(trimmed);
   const baseName = match?.[1] || "servitut";
   const extension = match?.[2] || "";
   let index = 2;
   while (usedNames.has(`${baseName} (${index})${extension}`)) {
      index += 1;
   }
   const uniqueName = `${baseName} (${index})${extension}`;
   usedNames.add(uniqueName);
   return uniqueName;
}

function groupMetadataByAkt(metadata: LLMMetadata[]): LLMMetadata[][] {
   const batchesByAkt = new Map<string, LLMMetadata[]>();
   metadata.forEach((entry) => {
      const key = entry.documentUrl || entry.id || entry.aktId || entry.document;
      const batch = batchesByAkt.get(key) ?? [];
      batch.push(entry);
      batchesByAkt.set(key, batch);
   });
   return Array.from(batchesByAkt.values());
}

async function runWithConcurrency<T>(
   items: T[],
   concurrency: number,
   worker: (item: T) => Promise<void>,
   signal?: AbortSignal,
) {
   let nextIndex = 0;
   const workerCount = Math.min(concurrency, items.length);
   const runners = Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length && !signal?.aborted) {
         const item = items[nextIndex];
         nextIndex += 1;
         await worker(item);
      }
   });
   await Promise.all(runners);
}

function attachAnalysisResult(payload: ServitutRedegoerelsePayload, result: LLMAnalysisResult): ServitutRedegoerelsePayload {
   const byId = new Map<string, LLMAnalysisResultItem>();
   const byAktId = new Map<string, LLMAnalysisResultItem>();
   result.items?.forEach((item) => {
      if (item.id) {
         byId.set(item.id, item);
      }
      if (item.aktId) {
         byAktId.set(item.aktId, item);
      }
   });
   return {
      ...payload,
      analysisResult: result,
      matrix: {
         ...payload.matrix,
         rows: payload.matrix.rows.map((row) => {
            if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
               const analysisItem = byId.get(row.key) ?? row.analysisItem;
               return { ...row, analysisItem };
            }
            const analysisItem = byId.get(row.key) ?? byAktId.get(row.analysisAktId) ?? row.analysisItem;
            return {
               ...row,
               analysisItem,
               selectedPages: row.selectedPages ?? (analysisItem ? normalizePages(analysisItem.pages_kept || []) : row.selectedPages),
            };
         }),
      },
   };
}

